<template>
    <div :style="profileContainerStyles" class="store-artist-profile-container">
        <div id="preloader" v-if="load" class="preloader"></div>
        <div ref="overview-wrapper" class="profile-overview-wrapper">
            <div class="overview-header">
                <div class="profile-pic">
                    <div v-if="!avatar" :class="formInputs.overview.wrapperClass">
                        <input
                            :ref="formInputs.overview.ref"
                            :id="formInputs.overview.id"
                            :type="formInputs.overview.type"
                            @change="handleAvatarUpload"
                            accept="image/*"
                            hidden="hidden"
                        />
                        <button @click="handleUploadAvatar" class="avatar-upload-btn"></button>
                    </div>
                    <div v-else class="profile-img">
                        <img :src="previewSrc(avatar)" alt="profile-img" />
                    </div>
                    <div class="profile-pic-action">
                        <button @click="handleUploadAvatar" class="upload-avatar-btn">
                            Upload
                        </button>
                        <button @click="handleRemoveAvatar" class="remove-avatar-btn">
                            Remove
                        </button>
                    </div>
                </div>
                <div class="profile-info">
                    <div v-if="bandName.value" class="artist-name">
                        {{ bandName.value }}
                    </div>
                    <div v-else class="artist-name">Your artist name</div>
                    <div v-if="bandSlug.value" class="artist-slug">
                        {{ `@${bandSlug.value.replaceAll(' ', '-')}` }}
                    </div>
                    <div v-else class="artist-slug">@your-hedlyner-handle</div>
                </div>
                <div class="profile-actions">
                    <button
                        v-if="!createMode && !shouldDisableView"
                        @click="redirectToViewArtistProfile"
                        class="view-profile-btn"
                    >
                        View Artist
                    </button>
                </div>
            </div>
            <div class="overview-footer">
                <div class="profile-completeness">
                    <div class="title">Complete your artist profile</div>
                    <div class="completeness">
                        <img :src="URLS.icons.questionMark" alt="question-mark" />
                        <span>Profile Completeness:</span>
                        <div v-if="!isMobile" class="progress">
                            <div
                                class="progress-bar"
                                :style="{ width: `${profileCompleteness}%` }"
                                role="progressbar"
                                :aria-valuenow="profileCompleteness"
                                aria-valuemin="0"
                                aria-valuemax="100"
                            ></div>
                        </div>
                        <div class="label">{{ renderProfileCompleteness }}%</div>
                    </div>
                </div>
                <div class="profile-actions">
                    <button @click="handleCancelChanges" class="cancel-btn">Cancel</button>
                    <button @click="handleSubmitChanges" class="submit-btn">
                        Save changes
                    </button>
                </div>
            </div>
        </div>
        <div class="page-divider"></div>
        <div v-if="showTestingMsg" class="testing-server-msg">
            Testing Server only: Please upload media files (avatar, images, video, audio) that
            are less than 2MB.
        </div>
        <div ref="details-wrapper" class="details-fields-wrapper">
            <div class="details-info">
                <div class="title">
                    <p>Details</p>
                </div>
                <div class="info">
                    <p>Plug in these details so venues can put you in the spotlight.</p>
                    <p>They'll be front and center on your profile.</p>
                </div>
            </div>
            <div class="details-fields">
                <div
                    v-for="(input, indx) in formInputs.details"
                    :key="indx"
                    :class="input.wrapperClass"
                >
                    <label :for="input.field" :class="input.error ? 'label-err' : ''">
                        {{ input.label }}
                    </label>
                    <input
                        v-if="input.vuelidate"
                        v-model="$v.formInputs.details.$each[indx].value.$model"
                        :type="input.type"
                        :name="input.field"
                        :placeholder="input.placeholder"
                        :maxlength="input.maxLength"
                        :min="input.min"
                        @input="validateInput('details', input.field, indx)"
                    />
                    <input
                        v-else
                        v-model="input.value"
                        :class="!input.horizontal && input.error ? 'input-err' : ''"
                        :type="input.type"
                        :name="input.field"
                        :placeholder="input.placeholder"
                        :maxlength="input.maxLength"
                        :min="input.min"
                        @wheel="handleWheelEvents($event, input.type)"
                        @input="validateInput('details', input.field)"
                        @keydown="
                            input.type === 'number'
                                ? handleFormatNumberInput($event, input.field)
                                : ''
                        "
                    />
                    <i
                        class="fas fa-dollar-sign dollar-sign"
                        v-if="indx === 3 && formInputs.details[3].value > 1"
                    ></i>
                    <div v-if="!input.horizontal && input.error" class="err-msg">
                        <img :src="URLS.icons.error" alt="err-icon" />
                        <p>
                            {{ input.errMsg }}
                        </p>
                    </div>
                </div>
            </div>
        </div>
        <div class="page-divider"></div>
        <div ref="bio-wrapper" class="bio-fields-wrapper">
            <div class="bio-info">
                <div class="title">
                    <p>Bio</p>
                </div>
                <div class="info">
                    <p>Help venues get to know you.</p>
                </div>
            </div>
            <div class="bio-fields">
                <div :class="formInputs.bio.wrapperClass">
                    <textarea
                        v-model="formInputs.bio.value"
                        :name="formInputs.bio.field"
                        :placeholder="formInputs.bio.placeholder"
                        @input="validateInput('bio', formInputs.bio.field)"
                        cols="30"
                        rows="10"
                    >
                    </textarea>
                    <div v-if="formInputs.bio.error" class="err-msg">
                        <img :src="URLS.icons.error" alt="err-icon" />
                        <p>
                            {{ formInputs.bio.errMsg }}
                        </p>
                    </div>
                </div>
            </div>
        </div>
        <div class="page-divider"></div>
        <div ref="socials-wrapper" class="socials-fields-wrapper">
            <div class="socials-info">
                <div class="title">
                    <p>Socials</p>
                </div>
                <div class="info">
                    <p>Link your Social Media Accounts.</p>
                </div>
            </div>
            <div class="socials-fields">
                <div
                    v-for="(input, indx) in formInputs.socials"
                    :key="indx"
                    :class="input.wrapperClass"
                >
                    <label :for="input.field" :class="input.error ? 'label-err' : ''">
                        {{ input.label }}
                    </label>
                    <input
                        v-model="input.value"
                        :type="input.type"
                        :name="input.field"
                        :maxlength="input.maxLength"
                        :placeholder="input.placeholder"
                        @input="validateInput('socials', input.field)"
                    />
                </div>

                <!-- @TODO: remove div block above and uncomment below once social providers are ready
                    <button
                        v-for="(btn, indx) in formInputs.socials"
                        :key="indx"
                        :class="socialBtnClass(btn)"
                        @click="handleSocialClick(btn)"
                        @mouseenter="handleSocialHover(btn)"
                        @mouseleave="handleSocialHover(btn)"
                    >
                        <img
                            v-if="btn.hovered && btn.active"
                            :src="URLS.icons.hoveredUnselect"
                            class="social-btn-unselect-icon"
                        />
                        <img
                            v-else-if="btn.hovered && !btn.active"
                            :src="URLS.icons.hoveredSelect"
                            class="social-btn-select-icon"
                        />
                        <img v-else-if="!btn.hovered" :src="btn.icon" class="social-btn-icons" />
                    </button>
                -->
            </div>
        </div>
        <div class="page-divider"></div>
        <div ref="genres-wrapper" class="genres-fields-wrapper">
            <div class="genres-info">
                <div class="title">
                    <p>Sound and Genres</p>
                </div>
                <div class="info">
                    <p>
                        Help venues find you with keywords. Choose words that best describe
                        your style and fanbase. You need at least one keyword to create your
                        profile page and apply for events. You can come back anytime and add
                        more.
                    </p>
                </div>
            </div>
            <div class="genres-fields">
                <div :class="genreInput.wrapperClass">
                    <input
                        v-model="keywordInput"
                        :class="genreInput.error ? 'input-err' : ''"
                        :type="genreInput.type"
                        :name="genreInput.field"
                        :placeholder="genreInput.placeholder"
                        @input="validateKeywordInput(genreInput)"
                        @keyup.enter="handleAddTag(genreInput.error)"
                    />
                </div>
                <div v-if="genreInput.error" class="err-msg">
                    <img :src="URLS.icons.error" alt="err-icon" />
                    <p>
                        {{ genreInput.errMsg }}
                    </p>
                </div>
            </div>
            <div v-if="keywords.length" class="genres-selected">
                <p class="label">The vibe is:</p>
                <div v-for="(tag, indx) in keywords" :key="indx" class="tag-wrapper">
                    <span @click="handleDeleteTag(indx)" class="tag">{{ tag }}</span>
                </div>
            </div>
            <div v-if="renderSuggestedKeywords.length" class="genres-suggested">
                <p class="label">Suggested keywords:</p>
                <div
                    v-for="(tag, indx) in renderSuggestedKeywords"
                    :key="indx"
                    class="tag-wrapper"
                >
                    <span class="tag" @click="handleSelectTag(tag)">
                        {{ tag.name }}
                    </span>
                </div>
            </div>
        </div>
        <div class="page-divider"></div>
        <div ref="medias-wrapper" class="medias-fields-wrapper">
            <div class="medias-info">
                <div class="title">
                    <p>Media</p>
                </div>
                <div class="info">
                    <p>
                        Attract venues by adding photos and videos. You can always add more,
                        but you need at least one photo to get started.
                    </p>
                </div>
            </div>
            <div class="medias-fields">
                <div :class="formInputs.medias.wrapperClass">
                    <input
                        :ref="formInputs.medias.ref"
                        :id="formInputs.medias.id"
                        :type="formInputs.medias.type"
                        @change="handleMediaUpload"
                        accept="video/*,image/*"
                        hidden="hidden"
                    />
                    <button @click="handleUploadMedia" class="media-upload-btn">
                        <img :src="URLS.icons.uploadMedia" alt="upload-media-btn" />
                    </button>
                </div>
                <div
                    v-if="uploadedMedia && uploadedMedia.length"
                    :style="mediaCarouselStyle"
                    class="media-carousel-container"
                >
                    <carousel
                        :mediaList="uploadedMedia"
                        :options="carouselStyles"
                        @currentMediaIndx="updateCurrentMediaIndx"
                    >
                        <p class="title"><a class="nostyle" href="#">View gallery</a></p>
                    </carousel>
                </div>
            </div>
        </div>
        <div class="page-divider"></div>
        <div ref="music-wrapper" class="music-fields-wrapper">
            <div class="music-info">
                <div class="title">
                    <p>Music</p>
                </div>
                <div class="info">
                    <p>
                        In order to apply to opportunities and to create your profile page, you
                        need to upload at least one music sample.
                    </p>
                </div>
            </div>
            <div v-if="uploadedMusic.length" class="music-samples">
                <MusicPlayer
                    :songs.sync="uploadedMusic"
                    @sync-title-change="handleChangeMusicTitle"
                    @sync-song-delete="handleDeleteMusic"
                />
            </div>
            <div class="music-fields">
                <input
                    :ref="formInputs.music.ref"
                    :id="formInputs.music.id"
                    :type="formInputs.music.type"
                    @change="handleMusicUpload"
                    accept="audio/mp3, audio/wav"
                    hidden="hidden"
                />
                <button @click="handleUploadMusic" class="music-upload-btn">
                    Upload My Music
                </button>
            </div>
        </div>
        <div class="page-divider"></div>
        <div ref="facilities-wrapper" class="facilities-fields-wrapper">
            <div class="facilities-info">
                <div class="title">
                    <p>Venues Equipment and Facilities</p>
                </div>
                <div class="info">
                    <p>What equipment do you need to be provided from a venue?</p>
                </div>
            </div>
            <div class="facilities-fields">
                <div v-if="addedEquipments.length" class="added-facilities-equipments">
                    <div
                        v-for="(setting, indx) in addedEquipments"
                        :key="indx"
                        :class="formInputs.facilities.wrapperClass"
                    >
                        <button @click="handleUnselect(setting)">
                            <img :src="URLS.icons.checkbox" alt="alt-checkbox" />
                        </button>
                        <label class="form-check-label" :for="setting.name">
                            {{ setting.label }}
                        </label>
                        <input
                            v-model="setting.description"
                            :type="formInputs.facilities.type"
                            :maxlength="formInputs.facilities.maxLength"
                            :placeholder="formInputs.facilities.placeholder"
                        />
                    </div>
                </div>
                <button @click="handleAddEquipments" class="add-equip-btn">
                    Add equipment
                </button>
            </div>
            <div class="facilities-settings-modal-container">
                <div v-if="showEquipmentsModal" class="facilities-settings-modal-wrapper">
                    <facilities-modal :settings="facilitySettings">
                        <template #header>
                            <p class="title">Venues equipment and Facilities</p>
                            <p class="subtitle">
                                What equpiment do you need to be provided form a venue?
                            </p>
                        </template>
                        <template #footer>
                            <div class="action-btns">
                                <button
                                    @click="handleSaveFacilitySettings"
                                    class="save-settings-btn"
                                >
                                    Set
                                </button>
                                <button
                                    @click="handleCancelFacilitySettings"
                                    class="cancel-settings-btn"
                                >
                                    Cancel
                                </button>
                            </div>
                        </template>
                    </facilities-modal>
                </div>
            </div>
        </div>
        <div class="page-divider"></div>
        <div ref="location-wrapper" class="location-fields-wrapper">
            <div class="location-info">
                <div class="title">
                    <p>Location</p>
                    <p class="subtitle">
                        Add your location details so venues know where you're based and how far
                        you are willing to travel.
                    </p>
                </div>
            </div>
            <div class="location-fields">
                <div
                    v-for="(input, indx) in formInputs.location"
                    :key="indx"
                    :class="input.wrapperClass"
                >
                    <label :for="input.field" :class="input.error ? 'label-err' : ''">
                        {{ input.label }}
                    </label>
                    <input
                        v-model="input.value"
                        :ref="input.ref"
                        :class="input.error ? 'input-err' : ''"
                        :type="input.type"
                        :name="input.field"
                        :placeholder="input.placeholder"
                        :maxlength="input.maxLength"
                        :min="input.min"
                        @wheel="handleWheelEvents($event, input.type)"
                        @keydown="
                            input.type === 'number'
                                ? handleFormatNumberInput($event, input.field)
                                : ''
                        "
                    />
                    <div v-if="input.error" class="err-msg">
                        <img :src="URLS.icons.error" alt="err-icon" />
                        <p>
                            {{ input.errMsg }}
                        </p>
                    </div>
                </div>
            </div>
            <div ref="google-map" id="google-map"></div>
        </div>
        <div class="page-divider"></div>
        <div class="action-btn-wrapper">
            <button @click="handleCancelChanges" class="cancel-changes-btn">Cancel</button>
            <button @click="handleSubmitChanges" class="save-changes-btn">Save changes</button>
        </div>
        <div class="confirmation-modal-container">
            <div v-if="showConfirmationModal" class="confirmation-modal-wrapper">
                <confirmation-modal :styles="confirmationOptions">
                    <template #header>
                        <p class="confirmation-header">Save before leaving</p>
                    </template>
                    <template #main>
                        <p class="confirmation-main">
                            You have changed some information in your profile. Do you want to
                            save changes before leaving?
                        </p>
                    </template>
                    <template #footer>
                        <div class="confirmation-footer">
                            <button @click="handleSubmitChanges" class="confirmation-save-btn">
                                Save changes
                            </button>
                            <button
                                @click="handleCancelConfirmation"
                                class="confirmation-cancel-btn"
                            >
                                Cancel
                            </button>
                        </div>
                    </template>
                </confirmation-modal>
            </div>
        </div>
        <div class="confirmation-modal-container">
            <div v-if="showRemoveSocialModal" class="confirmation-modal-wrapper">
                <confirmation-modal :styles="confirmationOptions">
                    <template #header>
                        <p class="confirmation-header">Disconnect socials</p>
                    </template>
                    <template #main>
                        <p class="confirmation-main">
                            You're about to disconnect {{ removeSocial.name }}
                        </p>
                    </template>
                    <template #footer>
                        <div class="confirmation-footer">
                            <button @click="handleRemoveSocial" class="confirmation-save-btn">
                                Yes, Please
                            </button>
                            <button
                                @click="handleCancelRemoveSocial"
                                class="confirmation-cancel-btn"
                            >
                                Cancel
                            </button>
                        </div>
                    </template>
                </confirmation-modal>
            </div>
        </div>
        <div
            v-show="showMissingProfileFieldsModal && missingProfileFieldsForModal.length"
            class="missing-profile-fields-modal-wrapper"
        >
            <MissingArtistProfileFieldsModal
                @close-missing-fields-modal="handleCloseMissingFieldsModal"
                :requiredProfileFields="missingProfileFieldsForModal"
            />
        </div>
        <div v-show="showModalOverlay" @click="handleOverlayClick" class="modal-overlay"></div>
    </div>
</template>
<script>
    import { api } from '@/helpers/api'
    import Google from '@/constants/google'
    import useKeywords from '@/composables/useKeywords.js'
    import useFormattedData from '@/composables/useFormattedData.js'
    import useNotification from '@/composables/useNotification.js'
    import useCookies from '@/composables/utils/useCookies.js'
    import useCheckScreen from '@/composables/useCheckScreen.js'
    import Carousel from '@/components/ui/carousels/Carousel.vue'
    import MusicPlayer from '@/components/ui/music-player/MusicPlayer.vue'
    import FacilitiesModal from '@/components/ui/modals/FacilitiesModal.vue'
    import ConfirmationModal from '@/components/ui/modals/ConfirmationModal.vue'
    import MissingArtistProfileFieldsModal from '@/components/user/profile/artist/parts/MissingProfileFieldsModal.vue'
    import { email } from 'vuelidate/lib/validators'

    const URLS = {
        icons: {
            questionMark: '/images/icons/question-mark.svg',
            checkbox: '/images/icons/checkbox.svg',
            marker: '/images/icons/marker-yellow.png',
            error: '/images/error.svg',
            uploadMedia: '/images/icons/upload-media-new.svg',
            hoveredUnselect: '/images/icons/hoveredUnselect.svg',
            hoveredSelect: '/images/icons/calendar/hoveredSelect.svg'
        },
        xhr: {
            fetch: {
                user: '/api/profile/v1/settings/user',
                media: '/api/profile/v1/user-profile/artist-medias',
                artist: '/api/profile/v1/user-profile/artist-info',
                social: '/api/profile/v1/user-profile/artist-social-links',
                music: '/api/profile/audios',
                facility: '/api/profile/v1/info/artist/facility-settings'
            },
            mutate: {
                info: '/api/profile/v1/info/artist',
                music: '/api/profile/update-audio',
                facility: '/api/profile/v1/info/artist/facility-settings',
                media: '/api/profile/update-media/artist',
                social: '/api/profile/social/link/add-artist',
                avatar: '/api/profile/update-avatar/artist',

                /** @TODO: remove field above and uncomment below once social providers are ready */
                //social: '/api/profile/social/link',
                create: '/api/profile/v1/info/artist/create',
                update: '/api/profile/v1/info/artist'
            }
        },
        redirect: {
            view: '/artists',
            landing: '/profiles'
        }
    }
    const suggestedTags = [
        {
            name: 'Jazz',
            selected: false
        },
        {
            name: 'Rap',
            selected: false
        },
        {
            name: 'Pop',
            selected: false
        },
        {
            name: 'Rock',
            selected: false
        },
        {
            name: 'Cover songs',
            selected: false
        },
        {
            name: 'Classic rock',
            selected: false
        }
    ]
    const infoFields = [
        'avatar',
        'band_name',
        'band_size',
        'band_title',
        'min_payout',
        'long_biography',
        'slug',
        'web_site',
        'keyword',
        'home_location',
        'lat',
        'lon',
        'travel_radius'
    ]

    /** @TODO: remove socialFields below once social providers are ready */

    const socialFields = ['socials']
    const facFields = ['facilities']
    const avatarFields = ['avatar']
    export default {
        name: 'StoreArtistProfile',
        components: {
            MusicPlayer,
            Carousel,
            FacilitiesModal,
            ConfirmationModal,
            MissingArtistProfileFieldsModal
        },
        props: {
            mode: {
                required: true,
                type: String,
                validator: (v) => ['edit', 'create'].includes(v)
            },
            artist: {
                required: false,
                type: Number
            }
        },
        validations: {
            formInputs: {
                details: {
                    $each: {
                        value: { email }
                    }
                }
            }
        },
        setup() {
            const {
                keywordInput,
                suggestedKeywords,
                renderSuggestedKeywords,
                keywords,
                validateKeywordInput,
                handleAddTag,
                handleSelectTag,
                handleDeleteTag,
                populateKeywords,
                clearKeywords
            } = useKeywords(suggestedTags)
            const { formatPhoneNumberInput, formatNumberInput } = useFormattedData()
            const { notification } = useNotification()
            const { isSmallMobile, isMobile, isTablet, isSmallLaptop } = useCheckScreen()
            const { setCookie, deleteCookie } = useCookies()
            return {
                notification,

                formatNumberInput,
                formatPhoneNumberInput,

                keywordInput,
                suggestedKeywords,
                renderSuggestedKeywords,
                keywords,
                validateKeywordInput,
                handleAddTag,
                handleSelectTag,
                handleDeleteTag,
                populateKeywords,
                clearKeywords,

                isSmallMobile,
                isMobile,
                isTablet,
                isSmallLaptop,

                setCookie,
                deleteCookie
            }
        },
        data: () => ({
            profile: null,
            slug: '',
            formInputs: {
                overview: {
                    wrapperClass: 'upload-avatar',
                    label: null,
                    field: 'avatar',
                    placeholder: null,
                    type: 'file',
                    ref: 'upload-avatar-input',
                    id: 'avatar-upload',
                    tag: 'input',
                    value: ''
                },
                details: [
                    {
                        wrapperClass: 'band-name-input',
                        label: 'Artist/Band*',
                        field: 'band_name',
                        placeholder: 'Artist/Band Name',
                        maxLength: '51',
                        min: null,
                        type: 'text',
                        tag: 'input',
                        value: '',
                        error: false,
                        errMsg: 'Please enter an artist name.',
                        validator: (v) => !!(v && v.length < 50)
                    },
                    {
                        wrapperClass: 'band-size-input',
                        label: 'Band Size',
                        field: 'band_size',
                        placeholder: '1 person',
                        maxLength: null,
                        min: '1',
                        type: 'number',
                        tag: 'input',
                        value: '',
                        error: false,
                        errMsg: 'Please enter a band size.',
                        validator: null
                    },
                    {
                        wrapperClass: 'band-title-input',
                        label: 'Profile Title',
                        field: 'band_title',
                        placeholder: 'Profile title',
                        maxLength: '51',
                        min: null,
                        type: 'text',
                        tag: 'input',
                        value: '',
                        error: null,
                        errMsg: null,
                        validator: null
                    },
                    {
                        wrapperClass: 'min-payout-input position-relative',
                        label: 'Minimum Payout',
                        field: 'min_payout',
                        maxLength: null,
                        min: 0,
                        placeholder: 'Minimum Payout',
                        type: 'number',
                        tag: 'input',
                        value: '',
                        error: false,
                        errMsg: 'Please add a valid payout in USD.',
                        validator: null
                    },
                    {
                        wrapperClass: 'artist-slug-input',
                        label: 'Hedlyner',
                        field: 'slug',
                        placeholder: 'Enter your handle',
                        maxLength: '51',
                        min: null,
                        type: 'text',
                        tag: 'input',
                        value: '',
                        horizontal: true,
                        error: false,
                        errMsg: 'Please enter a valid slug.',
                        validator: (v) => !v || /^[&a-zA-Z0-9-_ ]+$/.test(v)
                    },
                    {
                        wrapperClass: 'artist-website-input',
                        label: 'Website',
                        field: 'web_site',
                        placeholder: 'Enter your website',
                        maxLength: null,
                        min: null,
                        type: 'text',
                        tag: 'input',
                        value: '',
                        horizontal: true,
                        error: false,
                        errMsg: 'Please add a valid band website.',
                        validator: (v) =>
                            !v || /^(https?:\/\/)?(www\.)?[\w.-]+\.[\w.?=&%/-]{2,}$/.test(v)
                    }
                ],
                bio: {
                    wrapperClass: 'artist-bio-input',
                    label: '',
                    field: 'long_biography',
                    placeholder:
                        'Describe your style, your influences, career highlights, etc. ',
                    maxLength: null,
                    type: 'text',
                    tag: 'textarea',
                    value: '',
                    error: null,
                    errMsg: 'Please enter a shorter bio.',
                    validator: (v) => !v || v.length < 1000
                },
                socials: [
                    {
                        wrapperClass: 'instagram-input',
                        label: 'Instagram',
                        field: 'Instagram',
                        placeholder: 'Enter your URL: instagram.com/hedlyner',
                        maxLength: '100',
                        type: 'text',
                        tag: 'input',
                        value: '',
                        error: false,
                        errMsg: 'Please enter a valid Instagram handle.',
                        validator: (v) =>
                            !!(
                                !v ||
                                /^[a-zA-Z0-9-_]+$/.test(v) ||
                                /^(http:\/\/|https:\/\/)?(www.)?[Ii]nstagram.com\/.+$/.test(v)
                            ),

                        additionalFields: {
                            prefix: 'https://www.instagram.com/'
                        }
                    },
                    {
                        wrapperClass: 'facebook-input',
                        label: 'Facebook',
                        field: 'Facebook',
                        placeholder: 'Enter your URL: facebook.com/hedlyner',
                        maxLength: '100',
                        type: 'text',
                        tag: 'input',
                        value: '',
                        error: false,
                        errMsg: 'Please enter a valid Facebook handle.',
                        validator: (v) =>
                            !!(
                                !v ||
                                /^[a-zA-Z0-9-_]+$/.test(v) ||
                                /^(http:\/\/|https:\/\/)?(www.)?[Ff]acebook.com\/.+$/.test(v)
                            ),

                        additionalFields: {
                            prefix: 'https://www.facebook.com/'
                        }
                    },
                    {
                        wrapperClass: 'tiktok-input',
                        label: 'TikTok',
                        field: 'Tiktok',
                        placeholder: 'Enter your URL: tiktok.com/@hedlyner.official',
                        maxLength: '100',
                        type: 'text',
                        tag: 'input',
                        value: '',
                        error: false,
                        errMsg: 'Please enter a valid TikTok handle.',
                        validator: (v) =>
                            !!(
                                !v ||
                                /^[a-zA-Z0-9-_]+$/.test(v) ||
                                /^(http:\/\/|https:\/\/)?(www.)?[Tt]iktok.com\/@.+$/.test(v)
                            ),

                        additionalFields: {
                            prefix: 'https://www.tiktok.com/@'
                        }
                    },
                    {
                        wrapperClass: 'youtube-input',
                        label: 'YouTube',
                        field: 'Youtube',
                        placeholder: 'Enter your URL: youtube.com/hedlyner.official',
                        maxLength: '100',
                        type: 'text',
                        tag: 'input',
                        value: '',
                        error: false,
                        errMsg: 'Please enter a valid YouTube handle.',
                        validator: (v) =>
                            !!(
                                !v ||
                                /^[a-zA-Z0-9-_]+$/.test(v) ||
                                /^(http:\/\/|https:\/\/)?(www.)?[Yy]outube.com\/.+$/.test(v)
                            ),

                        additionalFields: {
                            prefix: 'https://www.youtube.com/'
                        }
                    },
                    {
                        wrapperClass: 'spotify-input',
                        label: 'Spotify',
                        field: 'Spotify',
                        placeholder: 'Enter your URL: spotify/artist/hedlyner',
                        maxLength: '100',
                        type: 'text',
                        tag: 'input',
                        value: '',
                        error: false,
                        errMsg: 'Please enter a valid Spotify handle.',
                        validator: (v) =>
                            !!(
                                !v ||
                                /^[a-zA-Z0-9-_]+$/.test(v) ||
                                /^(http:\/\/|https:\/\/)?open.[Ss]potify.com\/artist\/.+$/.test(
                                    v
                                )
                            ),

                        additionalFields: {
                            prefix: 'https://open.spotify.com/artist/'
                        }
                    },
                    {
                        wrapperClass: 'mix-input',
                        label: 'Mixcloud',
                        field: 'Mixcloud',
                        placeholder: 'Enter your URL: mixcloud.com/hedlyner',
                        maxLength: '100',
                        type: 'text',
                        tag: 'input',
                        value: '',
                        error: false,
                        errMsg: 'Please enter a valid Mixcloud handle.',
                        validator: (v) =>
                            !!(
                                !v ||
                                /^[a-zA-Z0-9-_]+$/.test(v) ||
                                /^(http:\/\/|https:\/\/)?(www.)?[Mm]ixcloud.com\/.+$/.test(v)
                            ),

                        additionalFields: {
                            prefix: 'https://www.mixcloud.com/'
                        }
                    },
                    {
                        wrapperClass: 'soundcloud-input',
                        label: 'SoundCloud',
                        field: 'Soundcloud',
                        placeholder: 'Enter your URL: soundcloud.com/hedlyner',
                        maxLength: '100',
                        type: 'text',
                        tag: 'input',
                        value: '',
                        error: false,
                        errMsg: 'Please enter a valid SoundCloud handle.',
                        validator: (v) =>
                            !!(
                                !v ||
                                /^[a-zA-Z0-9-_]+$/.test(v) ||
                                /^(http:\/\/|https:\/\/)?(www.)?[Ss]oundcloud.com\/.+$/.test(v)
                            ),

                        additionalFields: {
                            prefix: 'https://www.soundcloud.com/'
                        }
                    }
                    /** @TODO: remove array items above and uncomment below once social providers are ready */
                    // {
                    //     field: 'Instagram',
                    //     active: false,
                    //     hovered: false,
                    //     src: '/login/instagram',
                    //     icon: '/images/icons/instagram-grey.svg'
                    // },
                    // {
                    //     field: 'Facebook',
                    //     active: false,
                    //     hovered: false,
                    //     src: '/login/facebook',
                    //     icon: '/images/icons/facebook-grey.svg'
                    // },
                    // {
                    //     field: 'Tiktok',
                    //     active: false,
                    //     hovered: false,
                    //     src: '/login/tiktok',
                    //     icon: '/images/icons/tiktok-grey.svg'
                    // },
                    // {
                    //     field: 'Youtube',
                    //     active: false,
                    //     hovered: false,
                    //     src: '/login/youtube',
                    //     icon: '/images/icons/youtube-grey.svg'
                    // },
                    // {
                    //     field: 'Spotify',
                    //     active: false,
                    //     hovered: false,
                    //     src: '/login/spotify',
                    //     icon: '/images/icons/spotify-grey.svg'
                    // },
                    // {
                    //     field: 'Mixcloud',
                    //     active: false,
                    //     hovered: false,
                    //     src: '/login/mixcloud',
                    //     icon: '/images/icons/mix-grey.svg'
                    // }
                ],
                genres: {
                    wrapperClass: 'genre-input',
                    label: null,
                    field: 'keyword',
                    placeholder: 'Type keyword and hit enter to add',
                    maxLength: '30',
                    type: 'text',
                    tag: 'input',
                    ref: 'tagInput',
                    value: '',
                    error: false,
                    errMsg: 'Please enter tags with alphanumeric characters.',
                    validator: (v) => !!(!v || /^[ &a-zA-Z0-9-_]+$/.test(v))
                },
                medias: {
                    wrapperClass: 'upload-media',
                    label: null,
                    field: 'media',
                    placeholder: null,
                    type: 'file',
                    ref: 'upload-media-input',
                    id: 'media-upload',
                    additionalFields: {
                        medias: []
                    }
                },
                music: {
                    wrapperClass: null,
                    label: null,
                    field: 'music',
                    placeholder: null,
                    type: 'file',
                    id: 'music-upload',
                    ref: 'upload-music-input',
                    additionalFields: {
                        music: []
                    }
                },
                facilities: {
                    wrapperClass: 'facilities-setting',
                    label: null,
                    field: 'equipments',
                    placeholder: 'Specify: quantity, type, etc.',
                    type: 'text',
                    maxLength: '100',
                    additionalFields: {
                        facilitySettings: [
                            {
                                name: 'sound-boards',
                                label: 'Sound mixers/Sound boards',
                                selected: false,
                                description: ''
                            },
                            {
                                name: 'speakers',
                                label: 'Speakers',
                                selected: false,
                                description: ''
                            },
                            {
                                name: 'amplifiers',
                                label: 'Amplifiers',
                                selected: false,
                                description: ''
                            },
                            {
                                name: 'microphones',
                                label: 'Microphones',
                                selected: false,
                                description: ''
                            },
                            {
                                name: 'projectors-screens',
                                label: 'Projectors/Screens',
                                selected: false,
                                description: ''
                            },
                            {
                                name: 'video-switchers',
                                label: 'Video switchers',
                                selected: false,
                                description: ''
                            },
                            {
                                name: 'stage-lights',
                                label: 'Stage lights',
                                selected: false,
                                description: ''
                            },
                            {
                                name: 'wifi',
                                label: 'Wifi',
                                selected: false,
                                description: ''
                            },
                            {
                                name: 'elevated-stage',
                                label: 'Elevated stage',
                                selected: false,
                                description: ''
                            },
                            {
                                name: 'tech-staff',
                                label: 'Tech support staff',
                                selected: false,
                                description: ''
                            },
                            {
                                name: 'other',
                                label: 'Other',
                                selected: false,
                                description: ''
                            }
                        ],
                        currentSnapshot: {}
                    }
                },
                location: [
                    {
                        wrapperClass: 'location-input',
                        label: 'Enter an address*',
                        field: 'home_location',
                        placeholder: 'Suggestion Starting to type 123',
                        maxLength: '100',
                        type: 'text',
                        tag: 'input',
                        ref: 'autocomplete',
                        value: '',
                        error: false,
                        errMsg: 'Please enter a valid address from autocomplete.',
                        validator: null,
                        additionalFields: {
                            geoValues: {
                                lat: null,
                                lon: null
                            }
                        }
                    },
                    {
                        wrapperClass: 'radius-input',
                        label: 'Travel Radius*',
                        field: 'travel_radius',
                        placeholder: '1 mile',
                        maxLength: '4',
                        min: '1',
                        type: 'number',
                        tag: 'input',
                        value: '',
                        error: false,
                        errMsg: 'Please enter radius in miles.',
                        validator: (v) => parseFloat(v) > 0 && parseFloat(v) < 1500
                    }
                ]
            },
            carouselOptions: {
                index: 0,
                style: {
                    currentMedia: {
                        width: 260,
                        height: 260
                    },

                    otherMedia: {
                        width: 220,
                        height: 220
                    },

                    actionBtns: {
                        Xoffset: 175,
                        Yoffset: 145,
                        twoMediaXoffSet: 65
                    }
                }
            },
            confirmationOptions: {
                main: {
                    'margin-top': '16px'
                },
                footer: {
                    'margin-top': '48px'
                }
            },
            mapData: {
                defaultRadius: 0.5,
                map: null,
                autocomplete: null,
                marker: null,
                circle: null,
                apiLoaded: false,
                checkInterval: null
            },
            removeSocial: {
                id: null,
                name: null
            },
            userInfo: {},
            originalValues: {},
            load: false,
            showTestingMsg: false,
            showEquipmentsModal: false,
            showConfirmationModal: false,
            showRemoveSocialModal: false,
            showMissingProfileFieldsModal: false,
            shouldRedirect: false
        }),
        computed: {
            URLS: () => URLS,
            isTestingServer: () => window.location.hostname === '164.92.115.55',
            editMode: (self) => self.mode === 'edit',
            createMode: (self) => self.mode === 'create',
            profileContainerStyles: (self) => (self.isMobile ? { padding: '64px 16px' } : {}),
            shouldDisableView: (self) => {
                let disable = false

                if (typeof self.originalValues === 'object') return true

                for (const [key, value] of Object.entries(JSON.parse(self.originalValues))) {
                    if (Object.keys(self.minProfileRequiredInputs).includes(key)) {
                        if (key === 'avatar') {
                            if (!value) {
                                disable = true
                            }
                        } else if (
                            typeof value === 'object' &&
                            !Array.isArray(value) &&
                            value !== null &&
                            !Object.keys(value).length
                        ) {
                            disable = true
                        } else if (Array.isArray(value) && !value.length) {
                            disable = true
                        } else if (!value) {
                            disable = true
                        }
                    }
                }
                return disable
            },
            avatar: (self) => self.formInputs.overview.value,
            previewSrc: () => (fileObj) => {
                if (fileObj instanceof File) {
                    return URL.createObjectURL(fileObj)
                } else if (typeof fileObj === 'string') {
                    return fileObj
                } else {
                    return fileObj.src
                }
            },
            bandName: (self) => self.formInputs.details.find((i) => i.field === 'band_name'),
            bandSize: {
                get: function () {
                    return this.formInputs.details.find((i) => i.field === 'band_size').value
                },
                set: function (number) {
                    this.formInputs.details.find((i) => i.field === 'band_size').value = number
                }
            },
            bandSlug: (self) => self.formInputs.details.find((i) => i.field === 'slug'),
            maxFee: {
                get: function () {
                    return this.formInputs.details.find((i) => i.field === 'max_fee').value
                },
                set: function (number) {
                    this.formInputs.details.find((i) => i.field === 'max_fee').value = number
                }
            },
            MaxDuration: {
                get: function () {
                    return this.formInputs.details.find((i) => i.field === 'max_duration')
                        .value
                },
                set: function (number) {
                    this.formInputs.details.find((i) => i.field === 'max_duration').value =
                        number
                }
            },
            genreInput: (self) => self.formInputs.genres,
            facilitySettings: (self) =>
                self.formInputs.facilities.additionalFields.facilitySettings,
            facilitySnapshot: {
                get: function () {
                    return this.formInputs.facilities.additionalFields.currentSnapshot
                },
                set: function (settings) {
                    this.formInputs.facilities.additionalFields.currentSnapshot = settings
                }
            },
            uploadedMedia: (self) => self.formInputs.medias.additionalFields.medias,
            uploadedMusic: {
                get: function () {
                    return this.formInputs.music.additionalFields.music
                },
                set: function (newVal) {
                    this.formInputs.music.additionalFields.music = newVal
                }
            },
            addedEquipments: (self) => self.facilitySettings.filter((s) => s.selected),
            locationInput: (self) => self.formInputs.location,
            homeLocation: (self) =>
                self.locationInput.find((i) => i.field === 'home_location').value,
            travelRadius: {
                get: function () {
                    return this.locationInput.find((i) => i.field === 'travel_radius').value
                },
                set: function (radius) {
                    this.locationInput.find((i) => i.field === 'travel_radius').value = radius
                }
            },
            requiredInputs: (self) => ({
                band_name: self.bandName.value,
                home_location: self.homeLocation,
                lat: self.locationInput.find((i) => i.field === 'home_location')
                    .additionalFields.geoValues.lat,
                lon: self.locationInput.find((i) => i.field === 'home_location')
                    .additionalFields.geoValues.lon,
                travel_radius: self.travelRadius
            }),
            nonRequiredInputs: (self) => ({
                avatar: self.formInputs.overview.value,
                band_title: self.formInputs.details.find((i) => i.field === 'band_title')
                    .value,
                slug: self.bandSlug.value,
                long_biography: self.formInputs.bio.value,
                web_site: self.formInputs.details.find((i) => i.field === 'web_site').value,
                min_payout: self.formInputs.details.find((i) => i.field === 'min_payout')
                    .value,
                band_size: self.formInputs.details.find((i) => i.field === 'band_size').value,
                socials: self.formInputs.socials.reduce(
                    (a, c) => ({ ...a, [c.field]: c.value }),
                    {}
                ),

                /** @TODO: remove social field above and uncomment below once social providers are ready */

                // socials: self.formInputs.socials
                //     .filter((i) => typeof i.id === 'number')
                //     .map((i) => ({
                //         id: i.id,
                //         field: i.field,
                //         active: i.active
                //     })),
                keyword: self.keywords.join(','),
                medias: self.formInputs.medias.additionalFields.medias,
                music: self.formInputs.music.additionalFields.music,
                facilities: self.addedEquipments.reduce(
                    (a, c) => ({ ...a, [c.name]: c.description }),
                    {}
                )
            }),
            allInputs: (self) => ({
                ...self.requiredInputs,
                ...self.nonRequiredInputs
            }),
            minProfileRequiredInputs: (self) => ({
                ...self.requiredInputs,
                avatar: self.formInputs.overview.value,
                keyword: self.keywords.join(','),
                music: self.formInputs.music.additionalFields.music
            }),
            profileRequiredInputs: (self) => ({
                ...self.minProfileRequiredInputs,
                long_biography: self.formInputs.bio.value,
                socials: self.formInputs.socials
                    .filter((i) => !!i.value)
                    .reduce((a, c) => ({ ...a, [c.field]: c.value }), {}),
                /** @TODO: remove social field above and uncomment below once social providers are ready */

                // socials: self.formInputs.socials
                //     .filter((i) => typeof i.id === 'number')
                //     .map((i) => ({
                //         id: i.id,
                //         field: i.field,
                //         active: i.active
                //     })),
                medias: self.formInputs.medias.additionalFields.medias
            }),
            profileCompleteness: (self) => {
                let totalPerct = 0

                let totalRequiredValues = Object.values(self.requiredInputs)
                let totalNonRequiredValues = Object.values(self.nonRequiredInputs)

                let perctPerRequiredField = 50 / totalRequiredValues.length
                let perctPerNonRequiredField = 50 / totalNonRequiredValues.length

                for (const [key, value] of Object.entries(self.allInputs)) {
                    if (Object.keys(self.requiredInputs).includes(key)) {
                        if (value) {
                            totalPerct = totalPerct + perctPerRequiredField
                        }
                    } else {
                        if (
                            typeof value === 'object' &&
                            !Array.isArray(value) &&
                            value !== null &&
                            !Object.keys(value).length &&
                            !(value instanceof File)
                        ) {
                            continue
                        } else if (Array.isArray(value) && !value.length) {
                            continue
                        } else if (value) {
                            totalPerct = totalPerct + perctPerNonRequiredField
                        }
                    }
                }

                return totalPerct
            },
            renderProfileCompleteness: (self) => {
                if (Math.ceil(self.profileCompleteness) > 100) {
                    return 100
                }

                return Math.ceil(self.profileCompleteness)
            },
            requiredProfileFields: (self) => ({
                'Profile Picture':
                    !!self.formInputs.overview.value &&
                    !self.formInputs.overview.value.includes('default-artist-event.png') &&
                    !self.formInputs.overview.value.includes('default-artist.png'),
                'Artist/Band Name': !!self.bandName.value,
                'Sound & Genre': !!self.keywords.join(',').length,
                'Music samples': !!self.formInputs.music.additionalFields.music.length,
                Location: !!(
                    self.homeLocation &&
                    self.travelRadius &&
                    self.locationInput.find((i) => i.field === 'home_location')
                        .additionalFields.geoValues.lat &&
                    self.locationInput.find((i) => i.field === 'home_location')
                        .additionalFields.geoValues.lon
                )
            }),
            missingProfileFieldsForModal: (self) => {
                let missingFields = []

                for (const [key, val] of Object.entries(self.requiredProfileFields)) {
                    if (!val) missingFields.push(key)
                }

                return missingFields
            },
            showModalOverlay: (self) =>
                self.showEquipmentsModal ||
                self.showConfirmationModal ||
                self.showRemoveSocialModal ||
                (self.showMissingProfileFieldsModal &&
                    self.missingProfileFieldsForModal.length),
            mediaCarouselStyle: (self) =>
                self.isSmallMobile &&
                self.formInputs.medias.additionalFields.medias.length === 2
                    ? {
                          transform: 'translateX(10px)'
                      }
                    : {},
            carouselStyles: (self) => {
                if (self.isTablet) {
                    return {
                        ...self.carouselOptions.style,
                        actionBtns: {
                            Xoffset: 175,
                            Yoffset: 145,
                            twoMediaXoffSet: 125
                        }
                    }
                } else if (self.isSmallLaptop) {
                    return {
                        ...self.carouselOptions.style,
                        actionBtns: {
                            Xoffset: 175,
                            Yoffset: 145,
                            twoMediaXoffSet: 205
                        }
                    }
                } else {
                    return self.carouselOptions.style
                }
            },
            socialBtnClass: () => (btn) => {
                let _class = {}

                if (btn.active) {
                    _class['social-btn-active'] = true
                } else {
                    _class['social-btn-inactive'] = true
                }

                if (btn.hovered) {
                    _class['social-btn-hovered'] = true
                }

                return _class
            },
            modalsOpen: (self) =>
                self.showEquipmentsModal ||
                self.showConfirmationModal ||
                self.showRemoveSocialModal ||
                self.showMissingProfileFieldsModal
        },
        watch: {
            artist: {
                handler: function (artist) {
                    if (typeof artist === 'number') {
                        this.profile = artist
                    }
                },
                immediate: true
            },
            modalsOpen: {
                handler: function (open) {
                    if (open) {
                        document.body.classList.add('modal-open')
                    } else {
                        document.body.classList.remove('modal-open')
                    }
                },
                immediate: true
            },
            travelRadius: function (radius) {
                if (typeof radius === 'string' && radius.includes('.')) {
                    this.travelRadius = Math.ceil(parseFloat(radius))
                }
                if (radius) {
                    if (this.mapData.map && this.mapData.circle) {
                        this.setMapRadius(radius)
                    } else {
                        this.notification('error', 'Unable to connect to Google Maps API')
                    }

                    this.locationInput.find((i) => i.field === 'travel_radius').error = false
                } else {
                    this.locationInput.find((i) => i.field === 'travel_radius').error = true
                }
            },
            homeLocation: function (loc) {
                if (loc) {
                    this.locationInput.find((i) => i.field === 'home_location').error = false
                } else {
                    this.locationInput.find((i) => i.field === 'home_location').error = true
                }
            },
            keywordInput: function (keyword) {
                this.genreInput.value = keyword
            },
            'allInputs.lat': function (lat) {
                if (lat) {
                    if (this.mapData.map && this.mapData.circle) {
                        this.mapData.map.panTo({
                            lat: parseFloat(this.allInputs.lat),
                            lng: parseFloat(this.allInputs.lon)
                        })
                        this.mapData.circle.setCenter(
                            new google.maps.LatLng(
                                parseFloat(this.allInputs.lat),
                                parseFloat(this.allInputs.lon)
                            )
                        )
                    } else {
                        this.notification('error', 'Unable to connect to Google Maps API')
                    }
                }
            },
            'mapData.apiLoaded': function (loaded) {
                if (loaded) {
                    clearInterval(this.mapData.checkInterval)
                }
            }
        },
        async created() {
            await this.fetchUserInfo(true)

            if (this.editMode) {
                await this.initComponentData()
            }
            this.originalValues = this.copyCurrentValues()

            this.handleLaunchMissingFieldsModal()

            window.addEventListener('beforeunload', this.beforeWindowUnload)
        },
        mounted() {
            this.scrollToTop()

            if (this.isTestingServer) {
                this.showTestingMsg = true
            }

            this.mapData.checkInterval = setInterval(() => {
                if (window.google) {
                    this.mapData.apiLoaded = true
                    this.initialize()
                }
            })
        },
        beforeDestroy() {
            window.removeEventListener('beforeunload', this.beforeWindowUnload)
        },
        methods: {
            beforeWindowUnload(e) {
                this.load = true
                let {
                    artistAvatarChanges,
                    artistInfoChanges,

                    /** @TODO: remove artistSocialChanges below once social providers are ready */

                    artistSocialChanges,
                    artistMediaChanges,
                    artistMusicChanges,
                    artistFacChanges
                } = this.checkAllValues()

                if (
                    !this.shouldRedirect &&
                    (artistAvatarChanges.changed ||
                        artistInfoChanges.changed ||
                        /** @TODO: remove artistSocialChanges below once social providers are ready */

                        artistSocialChanges.changed ||
                        artistMediaChanges.changed ||
                        artistMusicChanges.changed ||
                        artistFacChanges.changed)
                ) {
                    e.preventDefault()
                    e.returnValue = ''
                    this.load = false
                }
            },
            redirectToViewArtistProfile() {
                this.$nextTick(() => {
                    window.location.href = `${this.URLS.redirect.view}/${this.slug}`
                })
            },
            redirectToProfilesLanding() {
                window.location.href = `${this.URLS.redirect.landing}`
            },
            async redirectAfterSubmit() {
                this.shouldRedirect = true
                this.originalValues = this.copyCurrentValues()

                if (this.shouldDisableView) {
                    this.redirectToProfilesLanding()
                } else {
                    this.redirectToViewArtistProfile()
                }
            },
            async initComponentData() {
                try {
                    this.load = true

                    let infoResp = await this.fetchArtistInfo()
                    let socialResp = await this.fetchArtistSocial()
                    let mediaResp = await this.fetchArtistMedia()
                    let musicResp = await this.fetchArtistMusic()
                    let facResp = await this.fetchArtistFac()
                    this.populateData(
                        infoResp.data,
                        socialResp.data,
                        mediaResp.data,
                        musicResp.data,
                        facResp.data
                    )
                } catch (e) {
                    console.error(e)
                    this.notification('error', 'Trouble initializing your user data.')
                } finally {
                    this.load = false
                }
            },
            initialize() {
                // eslint-disable-next-line
                this.mapData.map = new google.maps.Map(
                    this.$refs['google-map'],
                    Google.DEFAULT_MAP_OPT
                )

                // eslint-disable-next-line
                this.mapData.autocomplete = new google.maps.places.Autocomplete(
                    this.$refs['autocomplete'][0],
                    {
                        fields: ['geometry', 'formatted_address'],
                        types: ['geocode', 'establishment']
                    }
                )

                this.mapData.autocomplete.addListener('place_changed', this.handlePlaceChanged)

                this.initRadiusCircle(this.mapData.defaultRadius)
            },
            initRadiusCircle(radius) {
                radius = this.convertToMeters(radius)

                // eslint-disable-next-line
                this.mapData.marker = new google.maps.Marker({
                    map: this.mapData.map,
                    position: this.mapData.map.getCenter(),
                    title: 'some location',
                    icon: URLS.icons.marker
                })

                // eslint-disable-next-line
                this.mapData.circle = new google.maps.Circle({
                    map: this.mapData.map,
                    radius: radius,
                    strokeColor: '#cdeb09',
                    fillColor: '#cdeb09',
                    fillOpacity: 0.15
                })
                this.mapData.circle.bindTo('center', this.mapData.marker, 'position')
            },
            populateUserData() {
                if (this.createMode) {
                    if (this.userInfo.phone_number) {
                        this.bandPhone = this.userInfo.phone_number
                    }
                    if (this.userInfo.email) {
                        this.bandEmail = this.userInfo.email
                    }
                }
            },
            populateData(info, social, media, music, fac) {
                this.populateInfoData(info)
                this.populateSocialData(social)
                this.populateMediaData(media)
                this.populateMusicData(music)
                this.populateFacData(fac)

                this.handleLaunchMissingFieldsModal()
            },
            populateInfoData(data) {
                this.slug = data.slug
                for (const [key, value] of Object.entries(data)) {
                    if (key === 'avatar' && value != 'images/global/default.png') {
                        this.populateAvatar(value)
                    } else if (key === 'keyword') {
                        this.populateKeywords(value)
                    } else if (
                        key === 'home_location' ||
                        key === 'lat' ||
                        key === 'lng' ||
                        key === 'lon' ||
                        key === 'travel_radius'
                    ) {
                        this.populateLocation({ [key]: value })
                    } else if (key === 'long_biography') {
                        this.formInputs.bio.value = value
                    } else {
                        this.populateInput('details', key, value)
                    }
                }
            },
            populateAvatar(url) {
                if (!url.includes('/images/global/default.png')) {
                    this.formInputs.overview.value = url
                }
            },
            populateInput(input, key, value) {
                this.formInputs[input].forEach((i) => {
                    if (i.field === key) {
                        i.value = value
                    }
                })
            },
            populateSocialData(socials) {
                for (let i = 0; i < socials.length; i++) {
                    let data = socials[i]

                    this.formInputs.socials.forEach((i) => {
                        if (i.field === data.type) {
                            i.value = data.link
                        }
                    })
                }

                /** @TODO: remove for loop above and uncomment below once social providers are ready */

                // for (let i = 0; i < socials.length; i++) {
                //     let data = socials[i]

                //     this.formInputs.socials.forEach((b) => {
                //         if (b.field === data.type) {
                //             b.active = true

                //             this.$set(b, 'id', data.id)
                //         }
                //     })
                // }
            },
            populateMediaData(medias) {
                for (let i = 0; i < medias.length; i++) {
                    this.formInputs.medias.additionalFields.medias.push(medias[i])
                }
            },
            populateMusicData(songs) {
                this.uploadedMusic = songs
            },
            populateFacData(settings) {
                if (Array.isArray(settings) && !settings.length) return

                settings = JSON.parse(settings.settings)
                let settingsName = this.facilitySettings.map((s) => s.name)

                for (const [key, val] of Object.entries(settings)) {
                    let indx = settingsName.indexOf(key)

                    if (indx < 0) continue

                    this.facilitySettings[indx].selected = true
                    this.facilitySettings[indx].description = val
                }
            },
            populateLocation({ home_location, lat, lng, lon, travel_radius }) {
                if (home_location) {
                    this.locationInput.find((i) => i.field === 'home_location').value =
                        home_location
                } else if (lat) {
                    this.locationInput.find(
                        (i) => i.field === 'home_location'
                    ).additionalFields.geoValues.lat = lat
                } else if (lng) {
                    this.locationInput.find(
                        (i) => i.field === 'home_location'
                    ).additionalFields.geoValues.lon = lng
                } else if (lon) {
                    this.locationInput.find(
                        (i) => i.field === 'home_location'
                    ).additionalFields.geoValues.lon = lon
                } else if (travel_radius) {
                    this.locationInput.find((i) => i.field === 'travel_radius').value =
                        travel_radius
                }
            },
            async handleSocialClick(btn) {
                if (btn.active) {
                    this.handleShowRemoveSocialConfirmation(btn)
                } else {
                    this.deleteCookie('link-role')
                    this.setCookie({ name: 'link-role', value: 'artist', expireMinutes: 2 })
                    await this.handleSubmitChanges(null, { redirect: false })
                    this.shouldRedirect = true
                    window.location.href = btn.src
                }
            },
            async handleRemoveSocial() {
                const social = this.formInputs.socials.find(
                    (btn) => btn.id === this.removeSocial['id']
                )
                if (social) {
                    social.active = false
                    this.removeSocial['id'] = null
                    this.removeSocial['name'] = null
                    try {
                        this.load = true
                        await this.deleteArtistSocial(this.allInputs.socials)
                    } finally {
                        this.load = false
                    }
                }

                this.showRemoveSocialModal = false
            },
            handleSocialHover(btn) {
                btn.hovered = !btn.hovered
            },
            handleUploadAvatar() {
                this.$refs['upload-avatar-input'].click()
            },
            handleRemoveAvatar() {
                this.formInputs.overview.value = ''
            },
            handleAvatarUpload(e) {
                if (!e.target.files.length) return
                this.formInputs.overview.value = e.target.files[0]
            },
            handleUploadMedia() {
                this.$refs['upload-media-input'].click()
            },
            handleMediaUpload(e) {
                if (!e.target.files.length) return
                this.$set(
                    this.formInputs.medias.additionalFields.medias,
                    this.formInputs.medias.additionalFields.medias.length,
                    e.target.files[0]
                )
            },
            handleUploadMusic() {
                this.$refs['upload-music-input'].click()
                this.$refs['upload-music-input'].value = ''
            },
            handleMusicUpload(e) {
                if (!e.target.files.length) return

                if (this.checkForDuplicate(e.target.files[0])) {
                    return
                }

                this.$set(
                    this.formInputs.music.additionalFields.music,
                    this.formInputs.music.additionalFields.music.length,
                    e.target.files[0]
                )
            },
            async handleChangeMusicTitle(data) {
                await this.updateMusicTitle(data)
            },
            async handleDeleteMusic(data) {
                await this.deleteMusic(data)
            },
            handleFacilitiesSnapshot() {
                this.facilitySnapshot = this.nonRequiredInputs.facilities
            },
            handleAddEquipments() {
                this.scrollToTop()
                this.handleFacilitiesSnapshot()
                this.showEquipmentsModal = true
            },
            handleSaveFacilitySettings() {
                this.showEquipmentsModal = false
                this.scrollToView('facilities-wrapper')
            },
            handleCancelFacilitySettings() {
                this.clearFacilities()
                this.restoreFacilitySnapshot()
                this.showEquipmentsModal = false
                this.scrollToView('facilities-wrapper')
            },
            handleUnselect(setting) {
                setting.selected = false
                setting.description = ''
            },
            handleShowConfirmation() {
                this.showConfirmationModal = true
                this.scrollToTop()
            },
            handleFormatNumberInput(event, mode) {
                switch (mode) {
                    case 'band_size':
                        this.bandSize = this.formatNumberInput(event, this.bandSize)
                        break
                    case 'max_duration':
                        this.MaxDuration = this.formatNumberInput(event, this.MaxDuration)
                        break
                    case 'max_fee':
                        this.maxFee = this.formatNumberInput(event, this.maxFee)
                        break
                    case 'travel_radius':
                        this.travelRadius = this.formatNumberInput(event, this.travelRadius)
                        break
                    default:
                        return
                }
            },
            handleShowRemoveSocialConfirmation(btn) {
                this.removeSocial['id'] = btn.id
                this.removeSocial['name'] = btn.field

                this.showRemoveSocialModal = true
                this.scrollToTop()
            },
            async handleSubmitChanges(e, { redirect } = { redirect: true }) {
                this.showEquipmentsModal = false
                this.showConfirmationModal = false
                this.showMissingProfileFieldsModal = false
                this.showRemoveSocialModal = false

                if (this.validateAllInputs()) {
                    let {
                        artistAvatarChanges,
                        artistInfoChanges,
                        /** @TODO: remove artistSocialChanges below once social providers are ready */

                        artistSocialChanges,
                        artistMediaChanges,
                        artistMusicChanges,
                        artistFacChanges
                    } = this.checkAllValues()

                    try {
                        this.load = true
                        if (artistInfoChanges.changed) {
                            if (this.mode === 'create') {
                                const profileData = await this.createArtistProfile(
                                    this.prepInfoData(artistInfoChanges.data)
                                )
                                this.profile = profileData.data.id
                                this.slug = profileData.data.slug
                            } else {
                                const profileData = await this.updateArtistProfile(
                                    this.prepInfoData(artistInfoChanges.data)
                                )
                                this.slug = profileData.data.slug
                            }
                        }
                        if (artistAvatarChanges.changed) {
                            await this.updateArtistAvatar(artistAvatarChanges.data.avatar)
                        }
                        // if (artistInfoChanges.changed) {
                        //     await this.updateArtistInfo(artistInfoChanges.data)
                        // }

                        /** @TODO: remove artistSocialChanges below once social providers are ready */

                        if (artistSocialChanges.changed) {
                            await this.updateArtistSocial(artistSocialChanges.data.socials)
                        }
                        if (artistMediaChanges.changed) {
                            await this.updateArtistMedia(artistMediaChanges.data.medias)
                        }
                        if (artistMusicChanges.changed) {
                            await this.updateArtistMusic(artistMusicChanges.data.music)
                        }
                        if (artistFacChanges.changed) {
                            await this.updateArtistFac(artistFacChanges.data.facilities)
                        }

                        if (this.createMode) {
                            this.notification('success', 'Your artist profile is created.')
                        } else {
                            this.notification('success', 'Your artist profile is updated.')
                        }

                        this.scrollToTop()
                        if (redirect) {
                            this.redirectAfterSubmit()
                        } else {
                            return
                        }
                    } finally {
                        this.load = false
                    }
                }
            },
            handleCancelConfirmation() {
                this.showConfirmationModal = false
                this.restoreInitialValues()
            },
            handleCancelRemoveSocial() {
                this.removeSocial['id'] = null
                this.removeSocial['name'] = null
                this.showRemoveSocialModal = false
            },
            handleCancelChanges() {
                let {
                    artistAvatarChanges,
                    artistInfoChanges,
                    /** @TODO: remove artistSocialChanges below once social providers are ready */

                    artistSocialChanges,
                    artistMediaChanges,
                    artistMusicChanges,
                    artistFacChanges
                } = this.checkAllValues()

                if (
                    artistAvatarChanges.changed ||
                    artistInfoChanges.changed ||
                    /** @TODO: remove artistSocialChanges below once social providers are ready */

                    artistSocialChanges.changed ||
                    artistMediaChanges.changed ||
                    artistMusicChanges.changed ||
                    artistFacChanges.changed
                ) {
                    this.handleShowConfirmation()
                } else {
                    this.restoreInitialValues()
                }
            },
            handlePlaceChanged() {
                let place = this.mapData.autocomplete.getPlace()

                if (place.geometry && place.formatted_address) {
                    let lat = place.geometry.location.lat()
                    let lng = place.geometry.location.lng()

                    this.mapData.map.panTo({ lat, lng })

                    this.formInputs.location[0].value = place.formatted_address
                    this.formInputs.location[0].additionalFields.geoValues.lat = lat
                    this.formInputs.location[0].additionalFields.geoValues.lon = lng

                    this.mapData.circle.setCenter(new google.maps.LatLng(lat, lng))
                }
            },
            handleWheelEvents(event, inputType) {
                if (inputType !== 'number') return
                event.preventDefault()
            },
            handleOverlayClick() {
                this.showEquipmentsModal = false
                this.showConfirmationModal = false
            },
            handleCloseMissingFieldsModal() {
                this.showMissingProfileFieldsModal = false
            },
            handleLaunchMissingFieldsModal() {
                if (this.missingProfileFieldsForModal.length)
                    this.showMissingProfileFieldsModal = true
            },
            setMapRadius(radius) {
                this.setMapZoom(radius)
                this.mapData.circle.setRadius(this.convertToMeters(radius))
            },
            setMapZoom(radius) {
                let miles = Object.keys(Google.ZOOM_RADIUS_ENUM).map((k) => parseInt(k))
                let zoom = Google.DEFAULT_MAP_OPT.zoom

                for (let i = 0; i < miles.length; i++) {
                    let mile = miles[i]

                    if (radius <= mile) {
                        zoom = Google.ZOOM_RADIUS_ENUM[mile]
                        break
                    } else {
                        continue
                    }
                }

                this.mapData.map.setZoom(zoom)
            },
            checkForDuplicate(file) {
                for (let i = 0; i < this.uploadedMusic.length; i++) {
                    if (
                        this.uploadedMusic[i].name === file.name ||
                        this.uploadedMusic[i].name_file ===
                            file.name.substring(0, file.name.lastIndexOf('.'))
                    ) {
                        return true
                    }
                }
            },
            checkAllValues() {
                let artistInfoChanges = this.checkValuesChanged(infoFields)
                /** @TODO: remove artistSocialChanges below once social providers are ready */

                let artistSocialChanges = this.checkValuesChanged(socialFields)
                let artistMediaChanges = this.checkMediaChanged()
                let artistMusicChanges = this.checkMusicChanged()
                let artistFacChanges = this.checkValuesChanged(facFields)
                let artistAvatarChanges = this.checkValuesChanged(avatarFields)

                return {
                    artistAvatarChanges,
                    artistInfoChanges,
                    /** @TODO: remove artistSocialChanges below once social providers are ready */

                    artistSocialChanges,
                    artistMediaChanges,
                    artistMusicChanges,
                    artistFacChanges
                }
            },
            checkValuesChanged(fields) {
                let data = {}
                let originalData = {}
                let originalValues = JSON.parse(this.originalValues)

                for (let i = 0; i < fields.length; i++) {
                    data = { ...data, [fields[i]]: this.allInputs[fields[i]] }
                }

                for (let i = 0; i < fields.length; i++) {
                    originalData = { ...originalData, [fields[i]]: originalValues[fields[i]] }
                }

                if (JSON.stringify(data) === JSON.stringify(originalData)) {
                    return {
                        changed: false,
                        data: data
                    }
                } else {
                    return {
                        changed: true,
                        data: data
                    }
                }
            },
            checkMediaChanged() {
                return {
                    changed: this.uploadedMedia.some((m) => m instanceof File),
                    data: {
                        medias: this.uploadedMedia
                    }
                }
            },
            checkMusicChanged() {
                if (
                    this.uploadedMusic.some((m) => m instanceof File) ||
                    JSON.stringify(this.uploadedMusic) !==
                        JSON.stringify(JSON.parse(this.originalValues).music)
                ) {
                    return {
                        changed: true,
                        data: {
                            music: this.uploadedMusic
                        }
                    }
                } else {
                    return {
                        changed: false,
                        data: null
                    }
                }
            },
            prepInfoData(data) {
                data.keyword = [...new Set(data.keyword.split(','))].join(',')
                data.slug = data.slug.replaceAll(' ', '-')
                data.band_size =
                    data.band_size === '' || isNaN(parseInt(data.band_size))
                        ? null
                        : parseInt(data.band_size)
                data.min_payout =
                    data.min_payout === '' || isNaN(parseInt(data.min_payout))
                        ? null
                        : parseInt(data.min_payout)
                data.lat = parseFloat(data.lat)
                data.lon = parseFloat(data.lon)
                data.travel_radius = parseInt(data.travel_radius)

                if (data.web_site === '') {
                    data.web_site = null
                }

                return data
            },

            /** @TODO: remove method below once social providers are ready */

            prepSocialData(data) {
                let preppedData = {}

                for (let [key, value] of Object.entries(data)) {
                    if (value) {
                        const prefix = this.formInputs.socials.find((i) => i.field === key)
                            .additionalFields.prefix
                        const hasPrefix = value.includes(prefix)
                        const social = hasPrefix
                            ? value
                            : value
                                  .replace(new RegExp('/$'), '')
                                  .split('/')
                                  .slice(-1)[0]
                                  .replace(new RegExp('^@'), '')

                        value = hasPrefix ? value : prefix + social
                        preppedData[key] = value
                    } else {
                        preppedData[key] = null
                    }
                }
                return preppedData
            },
            prepDeleteSocialData(data) {
                return btoa(JSON.stringify(data.filter((s) => !s.active).map((s) => s.id)))
            },
            prepMediaData(data) {
                let formData = new FormData()

                for (let i = 0; i < data.length; i++) {
                    if (data[i] instanceof File) {
                        formData.append('files[]', data[i])
                    }
                }

                formData.append('_method', 'PUT')
                return formData
            },
            prepMusicData(data) {
                let formData = new FormData()
                let order = {}
                let orderName

                for (let i = 0; i < data.length; i++) {
                    if (data[i] instanceof File) {
                        orderName = data[i].name.split('.').slice(0, -1).join('.')
                        formData.append('audios[]', data[i])
                    } else {
                        orderName = data[i].name_file
                    }

                    order = {
                        ...order,
                        [orderName]: i
                    }
                }

                formData.append('order', JSON.stringify(order))
                formData.append('_method', 'PUT')

                return formData
            },
            updateCurrentMediaIndx(indx) {
                this.carouselOptions.index = indx
            },
            async fetchUserInfo(populate) {
                const data = await api({
                    method: 'get',
                    url: this.URLS.xhr.fetch.user
                })
                this.userInfo = data.data[0]

                if (populate) {
                    this.populateUserData()
                }
                return data
            },
            async fetchArtistInfo() {
                return await api({
                    method: 'get',
                    url: `${this.URLS.xhr.fetch.artist}/${this.profile}`
                })
            },
            async fetchArtistSocial() {
                return await api({
                    method: 'get',
                    url: `${this.URLS.xhr.fetch.social}/${this.profile}`
                })
            },
            async fetchArtistMedia() {
                return await api({
                    method: 'get',
                    url: `${this.URLS.xhr.fetch.media}/${this.profile}`
                })
            },
            async fetchArtistMusic() {
                return await api({
                    method: 'get',
                    url: `${this.URLS.xhr.fetch.music}/${this.profile}`
                })
            },
            async fetchArtistFac() {
                return await api({
                    method: 'get',
                    url: `${this.URLS.xhr.fetch.facility}/${this.profile}`
                })
            },
            async updateArtistInfo(data) {
                data = this.prepInfoData(data)

                return await api({
                    method: 'post',
                    url: `${this.URLS.xhr.mutate.info}/${this.profile}`,
                    data: {
                        _method: 'PUT',
                        ...data
                    }
                })
            },
            async updateArtistMedia(data) {
                return await api({
                    method: 'post',
                    url: `${this.URLS.xhr.mutate.media}/${this.profile}`,
                    data: this.prepMediaData(data)
                })
            },
            async updateArtistMusic(data) {
                return await api({
                    method: 'post',
                    url: `${this.URLS.xhr.mutate.music}/${this.profile}`,
                    data: this.prepMusicData(data)
                })
            },
            async updateMusicTitle(data) {
                try {
                    this.load = true
                    return await api({
                        method: 'post',
                        url: `${this.URLS.xhr.mutate.music}/${this.userInfo.id}`,
                        data: {
                            _method: 'PUT',
                            ...data
                        }
                    })
                } finally {
                    this.load = false
                }
            },
            async deleteMusic(data) {
                try {
                    this.load = true
                    return await api({
                        method: 'post',
                        url: `${this.URLS.xhr.mutate.music}/${this.profile}`,
                        data: {
                            _method: 'PUT',
                            delete_audios: data
                        }
                    })
                } finally {
                    this.load = false
                }
            },
            async updateArtistFac(data) {
                return await api({
                    method: 'post',
                    url: `${this.URLS.xhr.mutate.facility}/${this.profile}`,
                    data: {
                        _method: 'PUT',
                        settings: JSON.stringify(data)
                    }
                })
            },
            async createArtistProfile(data) {
                const form = new FormData()

                for (let key in data) {
                    form.append(key, data[key])
                }
                form.append('_method', 'PUT')

                return await api({
                    method: 'post',
                    url: `${this.URLS.xhr.mutate.create}/${this.userInfo.id}`,
                    data: form
                })
            },
            async updateArtistProfile(data) {
                const form = new FormData()

                for (let key in data) {
                    if (data[key] === null || data[key] === '') {
                        continue
                    }

                    form.append(key, data[key])
                }
                form.append('_method', 'PUT')

                return await api({
                    method: 'post',
                    url: `${this.URLS.xhr.mutate.update}/${this.profile}`,
                    data: form
                })
            },

            async updateArtistAvatar(data) {
                return await api({
                    method: 'post',
                    url: `${this.URLS.xhr.mutate.avatar}/${this.profile}`,
                    data: this.prepMediaData([data])
                })
            },
            /** @TODO: remove method below once social providers are ready */

            async updateArtistSocial(data) {
                return await api({
                    method: 'put',
                    url: `${this.URLS.xhr.mutate.social}/${this.profile}`,
                    data: this.prepSocialData(data)
                })
            },
            async deleteArtistSocial(data) {
                data = this.prepDeleteSocialData(data)
                return await api({
                    method: 'delete',
                    url: `${this.URLS.xhr.mutate.social}/${data}/${this.userInfo.id}`
                })
            },
            restoreInitialValues() {
                this.resetAllErrors()

                let originalValues = JSON.parse(this.originalValues)
                let infoData = infoFields.reduce(
                    (a, c) => ({
                        ...a,
                        [c]: originalValues[c]
                    }),
                    {}
                )

                let socialData = Object.entries(originalValues.socials).reduce((a, [k, v]) => {
                    return [
                        ...a,
                        {
                            type: k,
                            link: v
                        }
                    ]
                }, [])

                /** @TODO: remove socialData above and uncomment below once social providers are ready */

                // let socialData = originalValues.socials.reduce(
                //     (a, social) => [
                //         ...a,
                //         {
                //             type: social.field,
                //             id: social.id
                //         }
                //     ],
                //     []
                // )
                let mediaData = originalValues.medias
                let musicData = originalValues.music
                let facData = { settings: JSON.stringify(originalValues.facilities) }

                this.clearAvatar()
                this.clearSocials()
                this.clearKeywords()
                this.clearMedias()
                this.clearMusic()
                this.clearFacilities()
                this.clearLocation()

                this.populateInfoData(infoData)
                this.populateSocialData(socialData)
                this.populateMediaData(mediaData)
                this.populateMusicData(musicData)
                this.populateFacData(facData)
            },
            restoreFacilitySnapshot() {
                this.populateFacData({ settings: JSON.stringify(this.facilitySnapshot) })
            },
            restoreFacilitySettings() {
                const originalFacValues = JSON.parse(this.originalValues).facilities
                this.populateFacData({ settings: JSON.stringify(originalFacValues) })
            },
            resetAllErrors() {
                for (const val of Object.values(this.formInputs)) {
                    let inputGroup = val

                    if (Array.isArray(inputGroup)) {
                        inputGroup.forEach((i) => (i.error = false))
                    } else {
                        inputGroup.error = false
                    }
                }
            },
            clearAvatar() {
                this.formInputs.overview.value = ''
            },
            clearSocials() {
                this.formInputs.socials.forEach((i) => {
                    i.value = ''
                })
            },

            /** @TODO: remove method above and uncomment below once social providers are ready */

            // clearSocials() {
            //     this.formInputs.socials.forEach((i) => {
            //         i.active = false
            //         i.id = null
            //     })
            // },
            clearMedias() {
                this.uploadedMedia.splice(0)
            },
            clearMusic() {
                this.uploadedMusic.splice(0)
            },
            clearFacilities() {
                this.facilitySettings.forEach((s) => {
                    s.selected = false
                    s.description = ''
                })
            },
            clearLocation() {
                this.formInputs.location[0].value = ''
                this.formInputs.location[0].error = false
                this.formInputs.location[0].additionalFields.geoValues.lat = null
                this.formInputs.location[0].additionalFields.geoValues.lon = null

                this.formInputs.location[1].value = ''
                this.formInputs.location[1].error = false
            },
            validateAllInputs() {
                let detailsValid = this.validateInputs('details')

                /** @TODO: remove socialsValid below once social providers are ready */

                let socialsValid = this.validateInputs('socials')
                let keywordValid = !this.genreInput.error
                let locationValid = this.validateLocationInputs()

                if (!detailsValid) {
                    this.scrollToView('details-wrapper')
                    this.notification('error', 'Some of your profile details are incomplete.')
                } else if (!socialsValid) {
                    this.scrollToView('socials-wrapper')
                    this.notification('error', 'You need to enter a valid social link.')
                } else if (!keywordValid) {
                    this.scrollToView('genres-wrapper')
                    this.notification('error', 'You need to enter valid vibe keyword.')
                } else if (!locationValid) {
                    this.scrollToView('location-wrapper')
                    this.notification(
                        'error',
                        'You need to enter valid location and travel radius.'
                    )
                }

                /** @TODO: remove socialsValid below once social providers are ready */
                return detailsValid && socialsValid && keywordValid && locationValid
            },
            validateInputs(key) {
                let valid = true
                let inputs = this.formInputs[key]

                for (let i = 0; i < inputs.length; i++) {
                    let isValid

                    if (inputs[i].validator !== null) {
                        isValid = inputs[i].validator(inputs[i].value)
                    } else if (inputs[i].validator === null && inputs[i].vuelidate) {
                        this.$v.formInputs.details.$each[i].value.$touch()
                        isValid = !this.$v.formInputs.details.$each[i].value.$error
                    } else {
                        continue
                    }

                    if (isValid) {
                        inputs[i].error = false
                    } else {
                        inputs[i].error = true
                        valid = false
                    }
                }

                return valid
            },
            validateInput(key, field, indx = null) {
                let input

                if (Array.isArray(this.formInputs[key])) {
                    input = this.formInputs[key].find((i) => i.field === field)
                } else {
                    input = this.formInputs[key]
                }

                let valid

                if (input.validator !== null) {
                    valid = input.validator(input.value)
                    input.error = !valid
                } else if (input.validator === null && input.vuelidate && indx !== null) {
                    this.$v.formInputs.details.$each[indx].value.$touch()
                    input.error = this.$v.formInputs.details.$each[indx].value.$error
                } else {
                    return
                }
            },
            validateLocationInputs() {
                let valid = true

                if (
                    this.formInputs.location[0].value &&
                    this.formInputs.location[0].additionalFields.geoValues.lat &&
                    this.formInputs.location[0].additionalFields.geoValues.lon
                ) {
                    this.formInputs.location[0].error = false
                } else {
                    this.formInputs.location[0].error = true
                    valid = false
                }

                if (
                    this.formInputs.location[1].value &&
                    this.formInputs.location[1].validator(this.formInputs.location[1].value)
                ) {
                    this.formInputs.location[1].error = false
                } else {
                    this.formInputs.location[1].error = true
                    valid = false
                }

                return valid
            },
            convertToMinutes(seconds) {
                let finalSeconds = Math.floor(seconds % 60)

                if (finalSeconds >= 0 && finalSeconds < 10) {
                    finalSeconds = `0${finalSeconds}`
                }
                return `${(seconds / 60) | 0}:${finalSeconds}`
            },
            convertToMeters(miles) {
                miles = parseFloat(miles)

                return Math.floor(miles * 1609.34)
            },
            copyCurrentValues() {
                return JSON.stringify(this.allInputs)
            },
            scrollToTop() {
                window.scrollTo({
                    top: 0,
                    behavior: 'smooth'
                })
            },
            scrollToView(ref) {
                this.$refs[ref].scrollIntoView({
                    behavior: 'smooth'
                })
            }
        }
    }
</script>
<style lang="scss" scoped>
    $mediaCarouselBigBp: 1180px;
    $mediaCarouselMedBp: 800px;
    $smallMobile: 350px;
    $mobileMax: 600px;
    $tablet: 800px;
    .dollar-sign {
        color: #dedede;
        position: absolute;
        top: 52%;
        left: 1%;
        font-size: 16px;
    }

    @media screen and (max-width: $smallMobile) {
        .facilities-setting {
            input[type='text'] {
                max-width: 270px;
            }
        }
    }
    @media screen and (max-width: $mobileMax) {
        .overview-header {
            gap: 0px !important;
            align-items: flex-end;

            .profile-pic-action {
                justify-content: flex-start !important;
            }
            .profile-info {
                margin-top: 32px;
                width: fit-content !important;

                .artist-name {
                    font-size: 26px !important;
                }
            }
        }
        .profile-completeness {
            .title {
                font-size: 18px !important;
                font-weight: 650 !important;
            }
        }
        .facilities-setting {
            input[type='text'] {
                margin-left: 0px !important;
                margin-top: 20px;
            }
        }

        .confirmation-modal-wrapper {
            padding: 32px 16px !important;
        }
        .confirmation-header {
            font-size: 32px !important;
            text-align: left !important;
        }
        .confirmation-main {
            text-align: left !important;
        }

        .location-input {
            margin-top: 0px !important;
        }
    }

    @media screen and (max-width: $tablet) and (min-width: $mobileMax) {
        .facilities-setting {
            input[type='text'] {
                margin-left: 10px !important;
            }
        }
    }

    @media screen and (max-width: $mediaCarouselBigBp) {
        .medias-fields {
            gap: 40px;

            .upload-media {
                margin-left: auto;
                margin-right: auto;
            }
            .media-carousel-container {
                margin-left: auto;
                margin-right: auto;
                width: 100%;
            }
        }
    }

    .store-artist-profile-container {
        background: #121212;
        max-width: 1440px;
        margin-left: auto;
        margin-right: auto;
        display: flex;
        flex-direction: column;
        justify-content: flex-start;
        padding: 80px 60px 100px 60px;
        overflow: hidden;

        .input-err {
            border: 1px solid #e23453 !important;
        }

        .label-err {
            color: #e23453 !important;
        }

        p {
            margin-bottom: 0px;
        }
        button {
            border: none;
        }

        .err-msg {
            display: flex;
            flex-direction: row;
            gap: 10px;

            p {
                color: #e23453;
                font-family: Instrument Sans;
                font-size: 12px;
                font-style: normal;
                font-weight: 550;
                line-height: 120%;
                letter-spacing: -0.16px;
            }
        }

        .profile-overview-wrapper {
            display: flex;
            flex-direction: column;
            gap: 64px;

            .overview-header {
                display: flex;
                flex-direction: row;
                flex-wrap: wrap;
                gap: 32px;

                .profile-pic {
                    display: flex;
                    flex-direction: column;
                    gap: 16px;

                    .avatar-upload-btn {
                        border-radius: 12px;
                        background: url(/images/upload-profile.svg) no-repeat 50% 50% #acacac;
                        width: 244px;
                        height: 244px;
                    }

                    .profile-img {
                        img {
                            width: 244px;
                            height: 244px;
                            object-fit: cover;
                        }
                    }

                    .profile-pic-action {
                        display: flex;
                        flex-direction: row;
                        justify-content: center;
                        gap: 32px;

                        .upload-avatar-btn {
                            color: #cdeb09;
                            font-family: Instrument Sans;
                            font-size: 18px;
                            font-style: normal;
                            font-weight: 650;
                            line-height: 120%;
                            background: none;
                        }
                        .remove-avatar-btn {
                            color: #8b8b8b;
                            font-family: Instrument Sans;
                            font-size: 18px;
                            font-style: normal;
                            font-weight: 650;
                            line-height: 120%;
                            background: none;
                        }
                    }
                }
                .profile-info {
                    display: flex;
                    flex-direction: column;
                    justify-content: flex-start;
                    max-width: 575px;
                    width: fit-content;

                    .artist-name {
                        color: #dedede;
                        font-family: Instrument Sans;
                        font-size: 32px;
                        font-style: normal;
                        font-weight: 500;
                        line-height: 120%;
                        letter-spacing: -0.64px;
                    }

                    .artist-slug {
                        color: #acacac;
                        font-family: Instrument Sans;
                        font-size: 16px;
                        font-style: normal;
                        font-weight: 650;
                        line-height: 125%;
                        max-width: 135px;
                        overflow: hidden;
                        text-overflow: ellipsis;
                    }
                }
                .profile-actions {
                    margin-left: auto;

                    button {
                        background: none;
                        color: #8b8b8b;
                        font-family: Studio Pro;
                        font-size: 18px;
                        font-style: normal;
                        font-weight: 600;
                        line-height: 120%;
                        text-decoration-line: underline;
                    }
                }
            }

            .overview-footer {
                display: flex;
                flex-direction: row;
                justify-content: space-between;
                flex-wrap: wrap;
                gap: 32px;

                .profile-completeness {
                    display: flex;
                    flex-direction: column;
                    gap: 8px;

                    .title {
                        color: #dedede;
                        font-family: Instrument Sans;
                        font-size: 32px;
                        font-style: normal;
                        font-weight: 500;
                        line-height: 120%;
                        letter-spacing: -0.64px;
                    }

                    .completeness {
                        display: flex;
                        flex-direction: row;
                        justify-content: flex-start;
                        align-items: center;
                        gap: 8px;

                        img {
                            width: 12px;
                            height: 12px;
                        }

                        span {
                            margin-left: -4px;
                            color: #acacac;
                            font-family: Instrument Sans;
                            font-size: 16px;
                            font-style: normal;
                            font-weight: 650;
                            line-height: 125%;
                            text-wrap: nowrap;
                        }

                        .progress {
                            border-radius: 15px;
                            width: 200px;
                            height: 8px;
                            background: #121212;

                            .progress-bar {
                                background-color: #cdeb09 !important;
                            }
                        }

                        .label {
                            color: #dedede;
                            font-family: Instrument Sans;
                            font-size: 16px;
                            font-style: normal;
                            font-weight: 650;
                            line-height: 125%;
                        }
                    }
                }

                .profile-actions {
                    display: flex;
                    flex-direction: row;
                    gap: 8px;
                    flex-wrap: wrap-reverse;

                    .cancel-btn {
                        background: none;
                        color: #8b8b8b;
                        font-family: Studio Pro;
                        font-size: 18px;
                        font-style: normal;
                        font-weight: 600;
                        line-height: 120%;
                        text-decoration-line: underline;
                    }

                    .submit-btn {
                        border-radius: 100px;
                        background: #cdeb09;
                        padding: 11px 30px 12px 30px;
                        color: black;
                        font-family: Instrument Sans;
                        font-size: 18px;
                        font-style: normal;
                        font-weight: 650;
                        line-height: 120%;
                    }
                }
            }
        }

        .testing-server-msg {
            width: 607px;
            background: #646464;
            border-radius: 5px;
            text-align: center;
            color: #dedede;
            font-family: Instrument Sans;
            font-size: 14px;
            font-style: normal;
            font-weight: 650;
            line-height: 120%;
            padding: 5px 5px;
            margin-bottom: 40px;
        }

        .page-divider {
            background: rgba(255, 255, 255, 0.14);
            height: 1px;
            width: 100%;
            margin-top: 48px;
            margin-bottom: 48px;
        }

        .details-fields-wrapper,
        .bio-fields-wrapper,
        .socials-fields-wrapper,
        .genres-fields-wrapper,
        .medias-fields-wrapper,
        .music-fields-wrapper,
        .facilities-fields-wrapper,
        .location-fields-wrapper {
            .title {
                p {
                    color: #dedede;
                    font-family: Instrument Sans;
                    font-size: 32px;
                    font-style: normal;
                    font-weight: 500;
                    line-height: 120%;
                    letter-spacing: -0.64px;
                }
                .subtitle {
                    margin-top: 16px;
                    color: #acacac;
                    font-family: Instrument Sans;
                    font-size: 16px;
                    font-style: normal;
                    font-weight: 650;
                    line-height: 125%;
                }
            }

            .info {
                max-width: 603px;
                p {
                    color: #acacac;
                    font-family: Instrument Sans;
                    font-size: 16px;
                    font-style: normal;
                    font-weight: 650;
                    line-height: 125%;
                }
            }
        }

        .medias-fields-wrapper {
            .info {
                max-width: 417px !important;
            }
        }

        .details-fields-wrapper {
            display: flex;
            flex-direction: column;
            gap: 40px;

            .details-info {
                display: flex;
                flex-direction: column;
                gap: 12px;
            }

            .details-fields {
                display: flex;
                flex-direction: row;
                max-width: 603px;
                width: 100%;
                flex-wrap: wrap;
                gap: 20px;

                .band-name-input {
                    max-width: 424px;
                    width: 100%;
                }

                .band-size-input {
                    max-width: 159px;
                    width: 100%;
                }

                .band-title-input {
                    width: 100%;
                }

                .min-payout-input {
                    width: 100%;
                }

                .band-name-input,
                .band-size-input,
                .band-title-input,
                .min-payout-input {
                    display: flex;
                    flex-direction: column;
                    gap: 6px;

                    label {
                        color: #8b8b8b;
                        font-family: Instrument Sans;
                        font-size: 12px;
                        font-style: normal;
                        font-weight: 650;
                        line-height: 135%;
                        letter-spacing: 0.12px;
                    }

                    input {
                        padding: 12px 16px 16px 16px;
                        border-radius: 12px;
                        border: 1px solid #8b8b8b;
                        background: #262626;
                        color: #f5f5f5;
                        font-family: Instrument Sans;
                        font-size: 16px;
                        font-style: normal;
                        font-weight: 650;
                        line-height: 125%;
                    }

                    input::placeholder {
                        color: #646464;
                        font-family: Instrument Sans;
                        font-size: 16px;
                        font-style: normal;
                        font-weight: 650;
                        line-height: 125%;
                    }
                    input::-webkit-outer-spin-button,
                    input::-webkit-inner-spin-button {
                        -webkit-appearance: none;
                        margin: 0;
                    }

                    /* Firefox */
                    input[type='number'] {
                        -moz-appearance: textfield;
                    }
                }

                .artist-slug-input {
                    label {
                        background: url(/images/icons/hedlyner.svg) 0% 0% no-repeat transparent;
                    }
                }

                .artist-phone-input {
                    label {
                        background: url(/images/icons/phone.svg) 0% 0% no-repeat transparent;
                    }
                }

                .artist-email-input {
                    label {
                        background: url(/images/icons/email.svg) 0% 0% no-repeat transparent;
                    }
                }

                .artist-website-input {
                    label {
                        background: url(/images/icons/web.svg) 0% 0% no-repeat transparent;
                    }
                }

                .artist-slug-input,
                .artist-phone-input,
                .artist-email-input,
                .artist-website-input {
                    display: flex;
                    flex-direction: row;
                    width: 100%;
                    padding: 12px 16px 16px 16px;
                    border-radius: 12px;
                    border: 1px solid #8b8b8b;
                    background: #262626;

                    label {
                        width: 120px;
                        color: #dedede;
                        font-family: Instrument Sans;
                        font-size: 16px;
                        font-style: normal;
                        font-weight: 650;
                        line-height: 125%;
                        padding-right: 12px;
                        padding-left: 26px;
                        border: none;
                        border-right: 1px solid #8b8b8b;
                    }

                    input {
                        border: none;
                        padding-left: 12px;
                        background: #262626;
                        color: #f5f5f5;
                        font-family: Instrument Sans;
                        font-size: 16px;
                        font-style: normal;
                        font-weight: 650;
                        line-height: 125%;
                        width: 70%;
                    }

                    input::placeholder {
                        color: #646464;
                        font-family: Instrument Sans;
                        font-size: 16px;
                        font-style: normal;
                        font-weight: 650;
                        line-height: 125%;
                    }
                }
            }
        }

        .bio-fields-wrapper {
            display: flex;
            flex-direction: column;
            gap: 40px;

            .bio-info {
                display: flex;
                flex-direction: column;
                gap: 12px;
            }

            .bio-fields {
                display: flex;
                max-width: 603px;
                width: 100%;
                flex-direction: column;
                gap: 20px;

                .quick-bio-input,
                .artist-bio-input {
                    display: flex;
                    flex-direction: column;
                    gap: 6px;

                    label {
                        color: #8b8b8b;
                        font-family: Instrument Sans;
                        font-size: 12px;
                        font-style: normal;
                        font-weight: 650;
                        line-height: 135%;
                        letter-spacing: 0.12px;
                    }

                    input {
                        padding: 12px 16px 16px 16px;
                        border-radius: 12px;
                        border: 1px solid #8b8b8b;
                        background: #262626;
                        color: #f5f5f5;
                        font-family: Instrument Sans;
                        font-size: 16px;
                        font-style: normal;
                        font-weight: 650;
                        line-height: 125%;
                    }

                    input::placeholder {
                        color: #646464;
                        font-family: Instrument Sans;
                        font-size: 16px;
                        font-style: normal;
                        font-weight: 650;
                        line-height: 125%;
                    }

                    textarea {
                        padding: 12px 16px 16px 16px;
                        border-radius: 12px;
                        border: 1px solid #8b8b8b;
                        background: #262626;
                        color: #f5f5f5;
                        font-family: Instrument Sans;
                        font-size: 16px;
                        font-style: normal;
                        font-weight: 650;
                        line-height: 125%;
                    }

                    textarea::placeholder {
                        color: #646464;
                        font-family: Instrument Sans;
                        font-size: 16px;
                        font-style: normal;
                        font-weight: 650;
                        line-height: 125%;
                    }
                }
            }
        }

        .socials-fields-wrapper {
            display: flex;
            flex-direction: column;
            gap: 40px;

            .socials-info {
                display: flex;
                flex-direction: column;
                gap: 12px;
            }

            .socials-fields {
                display: flex;
                max-width: 603px;
                width: 100%;
                flex-direction: column;
                gap: 20px;

                .instagram-input {
                    label {
                        background: url(/images/icons/instagram.png) 0% 0% no-repeat
                            transparent;
                        background-size: 20px 20px;
                    }
                }
                .facebook-input {
                    label {
                        background: url(/images/icons/facebook.svg) 0% 0% no-repeat transparent;
                    }
                }
                .tiktok-input {
                    label {
                        background: url(/images/icons/tiktok.svg) 0% 0% no-repeat transparent;
                    }
                }
                .youtube-input {
                    label {
                        background: url(/images/icons/youtube.svg) 0% 0% no-repeat transparent;
                    }
                }
                .spotify-input {
                    label {
                        background: url(/images/icons/spotify.svg) 0% 0% no-repeat transparent;
                    }
                }
                .mix-input {
                    label {
                        background: url(/images/icons/mix.svg) 0% 0% no-repeat transparent;
                    }
                }
                .soundcloud-input {
                    label {
                        background: url(/images/icons/soundcloud.svg) 0% 0% no-repeat
                            transparent;
                    }
                }

                .instagram-input,
                .facebook-input,
                .tiktok-input,
                .youtube-input,
                .spotify-input,
                .mix-input,
                .soundcloud-input {
                    display: flex;
                    flex-direction: row;
                    width: 100%;
                    padding: 12px 16px 16px 16px;
                    border-radius: 12px;
                    border: 1px solid #8b8b8b;
                    background: #262626;

                    label {
                        width: 130px;
                        color: #dedede;
                        font-family: Instrument Sans;
                        font-size: 16px;
                        font-style: normal;
                        font-weight: 650;
                        line-height: 125%;
                        padding-right: 12px;
                        padding-left: 26px;
                        border: none;
                        border-right: 1px solid #8b8b8b;
                    }

                    input {
                        border: none;
                        padding-left: 12px;
                        background: #262626;
                        color: #f5f5f5;
                        font-family: Instrument Sans;
                        font-size: 16px;
                        font-style: normal;
                        font-weight: 650;
                        line-height: 125%;
                        width: 70%;
                    }

                    input::placeholder {
                        color: #646464;
                        font-family: Instrument Sans;
                        font-size: 16px;
                        font-style: normal;
                        font-weight: 650;
                        line-height: 125%;
                    }
                }
            }
        }

        /** @TODO: remove .social-fields-wrapper styles above and uncomment below once social providers are ready */

        // .socials-fields-wrapper {
        //     display: flex;
        //     flex-direction: column;
        //     gap: 40px;

        //     .socials-info {
        //         display: flex;
        //         flex-direction: column;
        //         gap: 12px;
        //     }

        //     .socials-fields {
        //         display: flex;
        //         max-width: 603px;
        //         width: 100%;
        //         flex-direction: row;
        //         gap: 8px;

        //         button {
        //             display: flex;
        //             justify-content: center;
        //             align-items: center;
        //             border-radius: 50%;
        //             border: none;
        //             width: 36px;
        //             height: 36px;

        //             .social-btn-icons {
        //                 width: 20px;
        //                 height: 20px;
        //             }
        //             .social-btn-select-icon,
        //             .social-btn-unselect-icon {
        //                 width: 36px;
        //                 height: 36px;
        //             }
        //         }

        //         .social-btn-hovered {
        //             padding: none !important;
        //             background: #cdeb09 !important;
        //         }
        //         .social-btn-active {
        //             background: #cdeb09;
        //         }
        //         .social-btn-inactive {
        //             background: #333333;
        //         }
        //     }
        // }

        .genres-fields-wrapper {
            display: flex;
            flex-direction: column;

            .genres-info {
                display: flex;
                flex-direction: column;
                gap: 12px;
            }

            .genres-fields {
                margin-top: 40px;
                max-width: 603px;
                width: 100%;

                .genre-input {
                    display: flex;
                    flex-direction: column;
                    gap: 6px;

                    input {
                        margin-bottom: 5px;
                        width: 100%;
                        padding: 12px 16px 16px 16px;
                        border-radius: 12px;
                        border: 1px solid #8b8b8b;
                        background: #262626;
                        color: #f5f5f5;
                        font-family: Instrument Sans;
                        font-size: 16px;
                        font-style: normal;
                        font-weight: 650;
                        line-height: 125%;
                    }

                    input::placeholder {
                        color: #646464;
                        font-family: Instrument Sans;
                        font-size: 16px;
                        font-style: normal;
                        font-weight: 650;
                        line-height: 125%;
                    }
                }
            }

            .genres-selected {
                margin-top: 24px;
                display: flex;
                flex-direction: row;
                flex-wrap: wrap;
                max-width: 1044px;
                width: 100%;
                min-height: 36px;
                height: 100%;
                gap: 8px;

                .label {
                    margin-bottom: 0px;
                    color: #dedede;
                    font-family: Instrument Sans;
                    font-size: 20px;
                    font-style: normal;
                    font-weight: 550;
                    line-height: 120%;
                    letter-spacing: -0.2px;
                }

                .tag-wrapper {
                    min-height: 40px;

                    span {
                        cursor: pointer;
                        padding: 7px 28px 9px 30px;
                        border-radius: 100px;
                        border: 1px solid #cdeb09;
                        background: #262626 url('/images/icons/delete-yellow.svg') no-repeat
                            92% 50%;
                        background-size: 8px 8px;
                        color: #cdeb09;
                        font-family: Instrument Sans;
                        font-size: 16px;
                        font-style: normal;
                        font-weight: 650;
                        line-height: 125%;
                    }
                }
            }

            .genres-suggested {
                margin-top: 26px;
                display: flex;
                flex-direction: row;
                flex-wrap: wrap;
                gap: 8px;
                max-width: 1044px;
                min-height: 80px;
                height: 100%;
                width: 100%;

                .label {
                    margin-bottom: 0px;
                    color: #8b8b8b;
                    font-family: Instrument Sans;
                    font-size: 20px;
                    font-style: normal;
                    font-weight: 550;
                    line-height: 120%;
                    letter-spacing: -0.2px;
                }

                .tag-wrapper {
                    min-height: 40px;

                    span {
                        cursor: pointer;
                        padding: 7px 30px 9px 30px;
                        border-radius: 100px;
                        border: 1px solid #8b8b8b;
                        background: #262626;
                        color: #8b8b8b;
                        font-family: Instrument Sans;
                        font-size: 16px;
                        font-style: normal;
                        font-weight: 650;
                        line-height: 125%;
                    }
                }
            }
        }

        .medias-fields-wrapper {
            display: flex;
            flex-direction: column;
            gap: 40px;

            .medias-info {
                display: flex;
                flex-direction: column;
                gap: 12px;
            }

            .medias-fields {
                display: flex;
                flex-direction: row;
                justify-content: space-between;
                align-items: center;
                flex-wrap: wrap;
                position: relative;

                .upload-media {
                    button {
                        border-radius: 15px;
                        border: 1px solid #646464;
                        max-width: 407px;
                        min-width: 278px;
                        padding: 0px;

                        img {
                            max-width: 407px;
                            min-width: 278px;
                            width: 100%;
                        }
                    }
                }

                .media-carousel-container {
                    .title {
                        margin-top: 20px;
                        a {
                            color: #acacac;
                            font-family: Instrument Sans;
                            font-size: 16px;
                            font-style: normal;
                            font-weight: 650;
                            line-height: 125%;
                            text-decoration: none;
                        }
                    }
                }
            }
        }

        .music-fields-wrapper {
            display: flex;
            flex-direction: column;
            max-width: 603px;
            width: 100%;
            gap: 40px;

            .music-info {
                display: flex;
                flex-direction: column;
                gap: 12px;
            }

            .music-fields {
                .music-upload-btn {
                    padding: 11px 30px 12px 30px;
                    border-radius: 100px;
                    background: #cdeb09;
                    color: black;
                    font-family: Instrument Sans;
                    font-size: 18px;
                    font-style: normal;
                    font-weight: 650;
                    line-height: 120%;
                }
            }
        }

        .facilities-fields-wrapper {
            display: flex;
            flex-direction: column;
            gap: 40px;

            .facilities-info {
                display: flex;
                flex-direction: column;
                gap: 16px;
            }

            .facilities-fields {
                display: flex;
                flex-direction: column;
                gap: 43px;
                align-items: flex-start;

                .added-facilities-equipments {
                    display: flex;
                    flex-direction: column;
                    justify-content: center;
                    align-items: flex-start;
                    gap: 12px;

                    .facilities-setting {
                        display: flex;
                        flex-direction: row;
                        justify-content: flex-start;
                        flex-wrap: wrap;
                        align-items: center;

                        button {
                            background: none;
                        }

                        .form-check-label {
                            margin-left: 8px;
                            color: #dedede;
                            font-family: Instrument Sans;
                            font-size: 18px;
                            font-style: normal;
                            font-weight: 650;
                            line-height: 120%;
                            min-width: 250px;
                        }
                        input[type='text'] {
                            margin-left: 32px;
                            padding: 12px 16px 16px 16px;
                            border-radius: 12px;
                            border: 1px solid #8b8b8b;
                            background: #262626;
                            color: #dedede;
                            font-family: Instrument Sans;
                            font-size: 16px;
                            font-style: normal;
                            font-weight: 650;
                            line-height: 125%;
                        }
                    }
                }
                .add-equip-btn {
                    padding: 11px 30px 12px 30px;
                    border-radius: 100px;
                    background: #cdeb09;
                    color: black;
                    font-family: Instrument Sans;
                    font-size: 18px;
                    font-style: normal;
                    font-weight: 650;
                    line-height: 120%;
                }
            }
            .facilities-settings-modal-container {
                position: fixed;
                top: 20%;
                left: 0;
                width: 100%;
                display: flex;
                justify-content: center;
                z-index: 1;

                .facilities-settings-modal-wrapper {
                    .title {
                        color: #dedede;
                        font-family: Instrument Sans;
                        font-size: 44px;
                        font-style: normal;
                        font-weight: 500;
                        line-height: 110%;
                        letter-spacing: -1.76px;
                    }
                    .subtitle {
                        margin-top: 16px;
                        color: #acacac;
                        font-family: Instrument Sans;
                        font-size: 16px;
                        font-style: normal;
                        font-weight: 650;
                        line-height: 125%;
                    }
                    .action-btns {
                        margin-top: 48px;
                        display: flex;
                        flex-direction: column;
                        align-items: center;
                        gap: 24px;
                        .save-settings-btn {
                            padding: 11px 30px 12px 30px;
                            max-width: 600px;
                            width: 100%;
                            border-radius: 100px;
                            background: #cdeb09;
                            color: black;
                            font-family: Instrument Sans;
                            font-size: 18px;
                            font-style: normal;
                            font-weight: 650;
                            line-height: 120%;
                        }
                        .cancel-settings-btn {
                            background: none;
                            color: #8b8b8b;
                            font-family: Studio Pro;
                            font-size: 18px;
                            font-style: normal;
                            font-weight: 600;
                            line-height: 120%;
                            text-decoration-line: underline;
                        }
                    }
                }
            }
        }
        .location-fields-wrapper {
            display: flex;
            flex-direction: column;
            gap: 12px;
            max-width: 603px;
            width: 100%;

            .location-fields {
                display: flex;
                flex-direction: row;
                justify-content: flex-start;
                flex-wrap: wrap-reverse;
                gap: 20px;

                .location-input {
                    max-width: 426px;
                    width: 100%;
                }
                .radius-input {
                    max-width: 157px;
                    width: 100%;
                }
                .location-input,
                .radius-input {
                    margin-top: 40px;
                    display: flex;
                    flex-direction: column;
                    gap: 6px;

                    label {
                        color: #8b8b8b;
                        font-family: Instrument Sans;
                        font-size: 12px;
                        font-style: normal;
                        font-weight: 650;
                        line-height: 135%;
                        letter-spacing: 0.12px;
                    }
                    input {
                        padding: 12px 16px 16px 16px;
                        border-radius: 12px;
                        border: 1px solid #8b8b8b;
                        background: #262626;
                        color: #f5f5f5;
                        font-family: Instrument Sans;
                        font-size: 16px;
                        font-style: normal;
                        font-weight: 650;
                        line-height: 125%;
                    }
                    input::placeholder {
                        color: #646464;
                        font-family: Instrument Sans;
                        font-size: 16px;
                        font-style: normal;
                        font-weight: 650;
                        line-height: 125%;
                    }
                }
            }
            #google-map {
                max-width: 603px;
                max-height: 310px;
                min-width: 280px;
                min-height: 300px;
                border-radius: 15px;
            }
        }
        .action-btn-wrapper {
            display: flex;
            flex-direction: row;
            justify-content: flex-end;
            gap: 8px;

            .save-changes-btn {
                padding: 11px 30px 12px 30px;
                border-radius: 100px;
                background: #cdeb09;
                color: black;
                font-family: Instrument Sans;
                font-size: 18px;
                font-style: normal;
                font-weight: 650;
                line-height: 120%;
            }
            .cancel-changes-btn {
                padding: 11px 30px 12px 30px;
                background: none;
                color: #8b8b8b;
                font-family: Studio Pro;
                font-size: 18px;
                font-style: normal;
                font-weight: 600;
                line-height: 120%;
                text-decoration-line: underline;
            }
        }
        .confirmation-modal-container {
            position: fixed;
            top: 20%;
            left: 0;
            display: flex;
            justify-content: center;
            z-index: 1;
            width: 100%;

            .confirmation-modal-wrapper {
                background: #262626;
                border-radius: 15px;
                padding: 48px 80px 48px 80px;
                max-width: 600px;
            }
        }
        .modal-overlay {
            position: fixed;
            width: 100vw;
            height: 100vh;
            background: black;
            opacity: 0.7;
            top: 0;
            left: 0;
        }
        .missing-profile-fields-modal-wrapper {
            position: fixed;
            display: flex;
            justify-content: center;
            top: 20%;
            left: 0;
            width: 100%;
            z-index: 1;
        }
        .confirmation-header {
            color: #f5f5f5;
            font-family: Instrument Sans;
            font-size: 44px;
            font-style: normal;
            font-weight: 500;
            line-height: 110%;
            letter-spacing: -1.76px;
            text-align: center;
        }
        .confirmation-main {
            color: #acacac;
            font-family: Instrument Sans;
            font-size: 18px;
            font-style: normal;
            font-weight: 650;
            line-height: 120%;
            text-align: center;
        }

        .confirmation-footer {
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;
            gap: 24px;

            .confirmation-save-btn {
                border-radius: 100px;
                background: #cdeb09;
                padding: 11px 30px 12px 30px;
                max-width: 180px;
                color: black;
                font-family: Instrument Sans;
                font-size: 18px;
                font-style: normal;
                font-weight: 650;
                line-height: 120%;
                text-wrap: nowrap;
            }
            .confirmation-cancel-btn {
                background: none;
                color: #8b8b8b;
                font-family: Studio Pro;
                font-size: 18px;
                font-style: normal;
                font-weight: 600;
                line-height: 120%;
                text-decoration-line: underline;
            }
        }
    }
</style>
